import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { DeckGL } from '@deck.gl/react';
import { SearchBox } from '@mapbox/search-js-react';
import { FaSearch, FaDrawPolygon, FaTrash, FaExpand, FaLayerGroup, FaSearchPlus, FaSearchMinus, FaMapMarkerAlt, FaFlagCheckered, FaTimes, FaCheck, FaWalking, FaCar, FaMotorcycle, FaBicycle, FaBus, FaPlay, FaCrosshairs, FaUndo, FaExternalLinkAlt, FaSpinner } from 'react-icons/fa';
import { Map } from 'react-map-gl';
import { ScatterplotLayer, PolygonLayer, PathLayer, TextLayer, GeoJsonLayer, IconLayer } from '@deck.gl/layers';
import StatCard from './CaseStudy/StatCard';
import { WebMercatorViewport } from '@deck.gl/core';
import { generateHexagons, getHexLayer, getTrafficLayer, getRouteLayer, getPOILayer, getSignalIconUris, getTransitRouteLayer, getTransitStopLayer, getIncomeHexLayer } from '../utils/mapUtils';
import TransitInvestmentPanel from './TransitInvestmentPanel';
import { fetchStaticJson } from '../utils/staticData';
import { buildCells as buildInvCells, orderLines, selectLines, summarize as summarizeInv, getInvestmentHexLayer, getInvestmentRouteLayer, SAME_BUDGET, sameBudgetUrl, SAME_BUDGET_COSTS_URL, sameBudgetPresets, fundedCost } from '../utils/transitInvestment';
import { getUpgradedLinesLayer } from '../utils/transitScenarioLayers';
import { cellToBoundary as h3CellToBoundary } from 'h3-js';
import NetworkEditorPanel from './NetworkEditorPanel';
import { getMapboxToken, isLiveSimulation, isDemo, getDemoMetro } from '../config';
import { losColor, vcDeltaColor, widthForCount, fetchManifest, fetchEdges, fetchTls, fetchMetrics, fetchComparison, fetchEdits, applyScenario, runSimulation, LOS_LEGEND, VC_DELTA_LEGEND, kmhToMph, fetchCostModel, estimateCost, formatUsd, fetchSafetyBaseline, pollJob, combineCost, fetchBakedDemand, buildTripFlowData, centerOfJunctions, FLOW_SCHEME as NE_FLOW_SCHEME } from '../utils/networkEditor';
import { FlowmapLayer, PickingType } from '@flowmap.gl/layers';
import { LocationFilterMode } from '@flowmap.gl/data';
import { VOLUME_LEGEND, fetchSchools, runScenario, fetchPrebaked, fetchPrebakedDay, fetchPrebakedTargets, fetchPrebakedComparison, fetchEdgeTraffic, hourSpan } from '../utils/trafficDataset';
import DayStrip from './DayStrip';
import CollapseToggle from './CollapseToggle';
import { PrebakedCpSection, PrebakedSzSection, PrebakedEditsPanel, scenarioHour } from './PrebakedScenarios';
import { DEFAULT_SERVICE_LEVEL, idAtLevel, packLevels, rankUrl } from '../utils/serviceLevels';
import { bandOutlines, treatedEdgeIds, BAND_OUTLINE } from '../utils/corridors';
import { onMap, useKeepOnMap } from '../hooks/useKeepOnMap';

const NE_SIGNAL_JUNCTION = '213437122'; // the one junction with a baked retiming
const TRANSIT_ROUTES_URL = '/data/transit/routes.geojson'; // read by Public Transport and Transit Investment
const NO_LINES = new Set();


// Below this the signal markers are plain dots, above it they are glyphs.
// 13 is where the measured median nearest-neighbour gap (14 px) stops being
// smaller than the marker itself; see the note on the ne-tls layer.
const NE_TLS_ICON_ZOOM = 13;

const TRANSPORT_TYPES = [
  { key: 'walk', label: 'Walk', costing: 'pedestrian', icon: FaWalking },
  { key: 'drive', label: 'Drive', costing: 'auto', icon: FaCar },
  { key: 'two-wheeler', label: '2-Wheeler', costing: 'motor_scooter', icon: FaMotorcycle },
  // { key: 'bike', label: 'Bike', costing: 'bicycle', icon: FaBicycle }, // TODO: re-enable when Valhalla bicycle routing is fixed
  { key: 'public-transport', label: 'Transit', costing: 'multimodal', icon: FaBus },
];
// Modes whose route depends on the hour: the road conditions along a drive, the departures of a transit trip.
const HOURLY_TRANSPORT = ['drive', 'two-wheeler', 'public-transport'];

const FACILITY_MIN_ZOOM = 14;

const SCENARIOS = [
  { key: 'plain', label: 'Base' },
  { key: 'congestion-pricing', label: 'Congestion Pricing' },
  { key: 'school-zones', label: 'School Zones' },
];

const DAYS_OF_WEEK = [
  { key: 'weekday', label: 'Weekday' },
  { key: 'weekend', label: 'Weekend' },
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
  { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' },
];

// What a metro costs before it can be used, in the order the service reports it.
// All four gate the map: `warm` (after step 2) can parse a network but a /run
// issued there queues behind the baseline inside the service.
const METRO_STEPS = ['Fetching inputs', 'Parsing network', 'Baseline routes', 'Baseline microsim'];
// A metro with no V/C layer in the serving layer gets one loaded after the
// simulation is ready; it is the same wait to the user, so it is a fifth step.
const PUBLISH_STEP = 'Loading traffic layer';
const METRO_STEP_NOTE = ['fetching inputs', 'parsing the network',
                         'routing the baseline', 'baseline simulation',
                         'loading the traffic layer'];

const NE_UNPAINTED_COLOR = [150, 158, 168, 200];

// What the map has already flown to. Keyed by metro, not by dataset: a metro
// whose V/C layer has just been published swaps its dataset for the produced
// run, and flying again there would look like the map jumping on its own.
const flyKey = (ds) => (ds?.cbsa ? `cbsa-${ds.cbsa}` : ds?.id);

// Where a demo host's map starts: its pack's focus.
const DEMO_VIEWS = { 41860: { longitude: -122.405161, latitude: 37.786465, zoom: 13 } };

function metroStepIndex(st) {
  if (!st) return -1;
  if (st.status === 'baseline_ready') return METRO_STEPS.length;
  if (st.status === 'warm') return st.step === 'baseline microsim' ? 3 : 2;
  if (st.status === 'loading') return st.step === 'parsing' ? 1 : 0;
  return -1;
}

// Fitted to the two metros measured on this box (15.3k edges → 13 s,
// 78.1k → 169 s). Superlinear, so a flat per-edge rate under-calls the big one.
function metroEta(edges) {
  if (!edges) return null;
  const s = 0.172 * Math.pow(edges / 1000, 1.58);
  return s < 45 ? 'under a minute' : `~${Math.round(s / 60)} min`;
}

// Zone outlines are drawn over every other layer: a road line on top hides the boundary.
const OVERLAY_IDS = ['pb-bands-outline', 'pb-cordon-line'];

function MapContainer({ onSearch, trafficData, datasets = [], activeDataset, onSelectDataset, routeData, routeSummary, isFullscreen, toggleSidebar, toggleFullscreen, showShortestPath, onRouteSearch, onCloseShortestPath, facilityData, onSelectFacility, showFacilities, onViewportBoundsChange, showMacroAnalysis, onCloseMacroAnalysis, showSchoolZones, onCloseSchoolZones, showCongestionPricing, onCloseCongestionPricing, showTrafficPanel, onCloseTrafficPanel, onRunTrafficDensity, onTrafficResult, showNetworkEditor, onCloseNetworkEditor, showPublicTransport, onTogglePublicTransport, showIncome, onToggleIncome, showTransitInvestment, onCloseTransitInvestment, metroStatus, metroLocked, onRetryMetro, viewHour = null, hourLock = null, onSelectHour, trafficHour = null, serviceLevel = DEFAULT_SERVICE_LEVEL, onServiceLevel }) {

  // A demo host opens on its metro, not on the Manhattan sample shown until the runs arrive.
  const [viewState, setViewState] = useState(() => DEMO_VIEWS[getDemoMetro()]
    || { longitude: -73.935242, latitude: 40.800610, zoom: 11 });
  const [layers, setLayers] = useState([]);
  const drawnLayers = useMemo(() => {
    const top = layers.filter((l) => l && OVERLAY_IDS.includes(l.id));
    return top.length ? [...layers.filter((l) => !l || !OVERLAY_IDS.includes(l.id)), ...top] : layers;
  }, [layers]);
  const [selectedHexagons, setSelectedHexagons] = useState(new Set());
  const [selectedPolygons, setSelectedPolygons] = useState({0: []});
  const hexIdToCoordinates = useRef(new window.Map());
  const [dimension, setDimension] = useState(null);
  const [hexResolution, setHexResolution] = useState(8);
  const [showHexagons, setShowHexagons] = useState(false);
  const [showHexControls, setShowHexControls] = useState(false);
  const [hexSuppressed, setHexSuppressed] = useState(false);
  const [gridBbox, setGridBbox] = useState(null);
  const [showDatasetPanel, setShowDatasetPanel] = useState(false);
  const [selectedMapboxId, setSelectedMapboxId] = useState(0);
  const hexControlsRef = useRef(null);
  const datasetPanelRef = useRef(null);
  const gridDebounceRef = useRef(null);
  const flownDatasetRef = useRef(null);
  const transitDataRef = useRef(null); // { routes: GeoJSON, stops: GeoJSON } — cached after first fetch
  const [transitMeta, setTransitMeta] = useState(null); // { hasBart, categories, agencyNames }

  // Shortest path states
  const [startPoint, setStartPoint] = useState(null); // { coord: [lng, lat], name: string, source: 'search' | 'map' }
  const [endPoint, setEndPoint] = useState(null);     // { coord: [lng, lat], name: string, source: 'search' | 'map' }

  // Transport type selection
  const [selectedTransport, setSelectedTransport] = useState(null); // key from TRANSPORT_TYPES

  // Map click selection states
  const [pendingPoint, setPendingPoint] = useState(null); // { coord: [lng, lat], type: 'start' | 'end' }

  // Draggable panel states
  const [panelPosition, setPanelPosition] = useState({ x: 0, y: 10 }); // x: offset from center, y: from top
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const panelRef = useRef(null);

  // Macro Analysis states
  const [macroScenario, setMacroScenario] = useState('plain');
  const [macroDrawingMode, setMacroDrawingMode] = useState('none'); // 'none' | 'analysis-area' | 'crz'
  const [macroDrawingVertices, setMacroDrawingVertices] = useState([]); // [[lng, lat], ...] while drawing
  const [macroAnalysisArea, setMacroAnalysisArea] = useState(null); // [[lng, lat], ...] closed polygon
  const [macroCrzArea, setMacroCrzArea] = useState(null); // [[lng, lat], ...] closed polygon
  const [macroResultData, setMacroResultData] = useState(null);
  const [macroRunning, setMacroRunning] = useState(false);
  const [macroRunResult, setMacroRunResult] = useState(null);
  const [macroDeltaFeatures, setMacroDeltaFeatures] = useState(null);
  const [szParams, setSzParams] = useState({ radiusM: 200, speedMph: 15 });
  // Traffic Density asks for its area explicitly, one way or the other.
  const [tdMode, setTdMode] = useState(null);   // 'area' | 'hex' | null
  const [tdArea, setTdArea] = useState(null);

  // Traffic Density draggable panel. Starts where the CSS used to pin it
  // (left 16 / top 70) so nothing moves until the user drags it.
  const [tdPanelPosition, setTdPanelPosition] = useState({ x: 16, y: 70 });
  const [isTdDragging, setIsTdDragging] = useState(false);
  const tdDragStartRef = useRef({ x: 0, y: 0 });
  const tdPanelRef = useRef(null);
  const [showMacroResultModal, setShowMacroResultModal] = useState(false);
  const [macroParams, setMacroParams] = useState({
    dayOfWeek: 'weekday',
    timeStart: 7,
    timeEnd: 19,
    vtts: 21.10,
    schoolStart: '08:00',
    schoolEnd: '17:00',
    zoneRadius: 200,
  });

  // Macro Analysis draggable panel
  const [macroPanelPosition, setMacroPanelPosition] = useState({ x: 0, y: 10 });
  const [isMacroDragging, setIsMacroDragging] = useState(false);
  const macroDragStartRef = useRef({ x: 0, y: 0 });
  const macroPanelRef = useRef(null);

  // School Zones states
  const [szData, setSzData] = useState(null);
  const [szLoading, setSzLoading] = useState(false);
  const [szShowAffected, setSzShowAffected] = useState(false);
  const [szShowHeatmap, setSzShowHeatmap] = useState(false);
  const [szShowNames, setSzShowNames] = useState(false);

  // School Zones draggable panel
  const [szPanelPosition, setSzPanelPosition] = useState({ x: 10, y: 10 });
  const [isSzDragging, setIsSzDragging] = useState(false);
  const szDragStartRef = useRef({ x: 0, y: 0 });
  const szPanelRef = useRef(null);

  // Congestion Pricing states
  const [cpData, setCpData] = useState(null);
  const [cpLoading, setCpLoading] = useState(false);
  const [cpShowTolled, setCpShowTolled] = useState(false);
  const [cpShowHeatmap, setCpShowHeatmap] = useState(false);
  const [cpShowReroute, setCpShowReroute] = useState(false);

  // Congestion Pricing draggable panel
  // Offset past the School Zones panel so the two don't stack when both are on
  const [cpPanelPosition, setCpPanelPosition] = useState({ x: 320, y: 10 });
  const [isCpDragging, setIsCpDragging] = useState(false);
  const cpDragStartRef = useRef({ x: 0, y: 0 });
  const cpPanelRef = useRef(null);

  // ---- Network Editor (Ithaca demo) ----
  const [neManifest, setNeManifest] = useState(null);
  const [neEdges, setNeEdges] = useState(null);        // raw edges.geojson (loaded once)
  const [neTls, setNeTls] = useState(null);
  const [neScenario, setNeScenario] = useState('baseline');
  const [neApplied, setNeApplied] = useState(null);    // { features, added } for active scenario
  const [neSummary, setNeSummary] = useState(null);
  const [neComparison, setNeComparison] = useState(null);   // active scenario's comparison.json
  const [neSelected, setNeSelected] = useState(null);       // {kind:'road'|'junction', ...}
  const [neEditKind, setNeEditKind] = useState(null);       // null|'closure'|'bypass'|'signal'
  const [neHoverId, setNeHoverId] = useState(null);         // edge id under cursor
  const [neEdits, setNeEdits] = useState({ removed: [], added: [], attrs: {} });
  const [neRunning, setNeRunning] = useState(false);        // simulation in flight
  const [neRunError, setNeRunError] = useState(null);
  const [neRunResult, setNeRunResult] = useState(null);     // metrics returned by the run
  const [neLoading, setNeLoading] = useState(false);
  // A demand override REPLACES the baseline rather than staging, so it carries its own
  // run result (neDemandBaseline) instead of landing in neRunResult.
  const [neDemand, setNeDemand] = useState(null);
  const [neDemandReport, setNeDemandReport] = useState(null);  // its validate report
  const [neDemandBaseline, setNeDemandBaseline] = useState(null);
  const [neDemandRunning, setNeDemandRunning] = useState(false);
  const [neDemandError, setNeDemandError] = useState(null);
  // Trips before the demand multiplier; metrics.json counts vehicles after the scale.
  const [neBakedTrips, setNeBakedTrips] = useState(null);
  // ---- Trip flows ----
  // neBakedDemand: the edit service's placed copy of the baked matrix, fetched once.
  const [neShowFlows, setNeShowFlows] = useState(false);
  const [neBakedDemand, setNeBakedDemand] = useState(null);
  const [neFlowLoading, setNeFlowLoading] = useState(false);
  const [neFlowError, setNeFlowError] = useState(null);
  // The zone whose flows are shown alone, or null for all of them.
  const [neFlowZone, setNeFlowZone] = useState(null);
  const [neFlowMode, setNeFlowMode] = useState('all');
  // Fade strength for the quiet end of the flow ramp. flowmap.gl scales it by
  // 2/100 into an HCL-lightness multiplier, so 50 already blacks the smallest
  // flows out entirely and the useful range stops well short of 100.
  const [neFlowFade, setNeFlowFade] = useState(15);
  const [neShowSignals, setNeShowSignals] = useState(true);
  // Bumped by Reset; used as NeDemandSection's `key` so clearing the demand remounts it.
  const [neDemandResetKey, setNeDemandResetKey] = useState(0);
  // Baked SSM baseline (conflict counts, unedited network + baked demand). Once a custom
  // demand is applied, the demand baseline's safety takes over as the "before".
  const [neSafetyBaseline, setNeSafetyBaseline] = useState(null);
  // Background conflict analysis for the run on screen; only fills the Safety section.
  const [neSafetyJob, setNeSafetyJob] = useState(null);   // {status, error?}
  // Which of the two Run buttons started the job on screen: 'edit' | 'demand'.
  const [neJobOwner, setNeJobOwner] = useState(null);
  // Cancels the poller for a superseded run.
  const neJobStop = useRef(null);
  // Cost model fetched once and looked up locally, so staging an edit costs no round trip.
  const [costModel, setCostModel] = useState(null);
  const [nePanelPosition, setNePanelPosition] = useState({ x: 10, y: 10 });
  const [isNeDragging, setIsNeDragging] = useState(false);
  const neDragStartRef = useRef({ x: 0, y: 0 });
  const nePanelRef = useRef(null);

  // The metro every Network Editor request names; the data source picker owns it.
  const neCbsa = activeDataset?.cbsa ? String(activeDataset.cbsa) : null;
  const [neLoadedCbsa, setNeLoadedCbsa] = useState(null);
  // `warm` is enough: the network is parsed, so the map and the editor work
  // while the baseline is still being computed.
  // The label carries the scenario ("Ithaca, NY — Baseline"); the editor and the
  // load strip are about the metro, not the run.
  const metroName = activeDataset && String(activeDataset.label).split(' \u2014 ')[0];
  const multiHour = (activeDataset?.hours || []).length > 1;
  // The hour picker shows only while something on the map depends on the hour; the
  // hour chosen is kept while it is hidden.
  const hourInUse = !!(showTrafficPanel || (trafficData && Object.keys(trafficData).length > 0)
    || showCongestionPricing || (showShortestPath && HOURLY_TRANSPORT.includes(selectedTransport)));
  const showDayStrip = multiHour && viewHour !== null && !showNetworkEditor && hourInUse;

  // Each floating panel and legend folds to its title bar; closing one unfolds it for
  // the next time it opens.
  const [collapsed, setCollapsed] = useState({});
  const folded = (id) => (collapsed[id] ? 'panel-collapsed' : '');
  const foldToggle = (id, name) => (
    <CollapseToggle collapsed={!!collapsed[id]} name={name}
      onToggle={() => setCollapsed((c) => ({ ...c, [id]: !c[id] }))} />
  );
  const shownPanels = {
    sp: showShortestPath, sz: showSchoolZones, cp: showCongestionPricing, td: showTrafficPanel,
    inv: showTransitInvestment, income: showIncome, transit: showPublicTransport,
    traffic: !!(trafficData && Object.keys(trafficData).length), route: !!routeSummary,
  };
  const shownKey = Object.keys(shownPanels).filter((k) => shownPanels[k]).join(',');
  useEffect(() => {
    const open = new Set(shownKey ? shownKey.split(',') : []);
    setCollapsed((c) => {
      const off = Object.keys(c).filter((k) => c[k] && !open.has(k));
      if (!off.length) return c;
      const next = { ...c };
      off.forEach((k) => { delete next[k]; });
      return next;
    });
  }, [shownKey]);
  const neWarm = !!neCbsa && metroStatus?.cbsa === neCbsa
    && (metroStatus.status === 'warm' || metroStatus.status === 'baseline_ready');
  // With no service at all the backend still has its checked-in fixture, so the
  // editor asks anyway rather than waiting for a metro that will never load.
  const neServiceFailed = metroStatus?.cbsa === neCbsa && metroStatus?.status === 'failed';
  const metroStep = metroStepIndex(metroStatus);
  const metroSteps = metroStatus?.publish?.status === 'pending'
    ? [...METRO_STEPS, PUBLISH_STEP] : METRO_STEPS;
  // A metro whose V/C layer could not be loaded is still editable and still
  // simulable, so the picker says what is missing instead of hiding it.
  const publishError = (ds) =>
    (ds.cbsa && metroStatus?.cbsa === String(ds.cbsa)
      && metroStatus.publish?.status === 'failed' ? metroStatus.publish.error : null);
  // Exact once the network is parsed; before that the picker's own estimate.
  const metroEdges = metroStatus?.edges || activeDataset?.edges
    || activeDataset?.summary?.edges || null;

  useEffect(() => { fetchCostModel(neCbsa).then(setCostModel); }, [neCbsa]);

  // ---- Scenarios computed ahead, for a metro too large to simulate on request ----
  // `prebakedOnly`: the metro has nothing else -- no live edits, no drawn zones.
  const liveSim = isLiveSimulation();
  const prebakedOnly = activeDataset?.prebaked?.live === false || (!liveSim && !!activeDataset?.prebaked);
  const [prebaked, setPrebaked] = useState(null);
  const [pbChoice, setPbChoice] = useState(null);         // { panel: 'cp'|'sz'|'ne', id }
  const [pbResult, setPbResult] = useState(null);         // /compare, or { loading } / { error }
  const [pbTargets, setPbTargets] = useState(null);       // the chosen edit's edges
  const [pbCandidates, setPbCandidates] = useState(null); // every road and signal edit's edges
  const [pbDay, setPbDay] = useState(null);               // /prebaked/day, or { loading } / { error }
  const [pbBandsOn, setPbBandsOn] = useState(true);       // a route-cost scenario's bands on the map
  const [pbBands, setPbBands] = useState(null);           // { outlines } of the bands, { loading } or { error }
  const pbBandsAsk = useRef(0);
  const pbBandsFly = useRef(false);                       // the next bands to arrive move the map over them
  const pbAsk = useRef(0);
  const pbResultAsk = useRef(0);
  const pbSz = prebaked?.edits?.find((e) => e.kind === 'school_zone') || null;
  const pbNeEdits = useMemo(
    () => (prebaked?.edits || []).filter((e) => e.kind !== 'school_zone'), [prebaked]);

  useEffect(() => {
    pbAsk.current += 1;
    pbResultAsk.current += 1;
    setPrebaked(null);
    setPbChoice(null);
    setPbResult(null);
    setPbTargets(null);
    setPbCandidates(null);
    setPbDay(null);
    if (!activeDataset?.prebaked || !activeDataset.cbsa) return undefined;
    let cancelled = false;
    const cbsa = activeDataset.cbsa;
    const severalHours = (activeDataset.hours || []).length > 1;
    fetchPrebaked(cbsa)
      .then((p) => {
        if (cancelled) return;
        setPrebaked(p);
        if (!p.cp || !severalHours) return;
        setPbDay({ loading: true });
        fetchPrebakedDay(cbsa)
          .then((d) => { if (!cancelled) setPbDay(d); })
          .catch((err) => {
            if (!cancelled) setPbDay({ error: `The totals for the day could not be loaded (${err.message}).` });
          });
      })
      .catch((err) => console.error('Failed to load the pre-computed scenarios:', err));
    return () => { cancelled = true; };
  }, [activeDataset?.id]);

  const stripEntries = useMemo(() => {
    if (!showCongestionPricing || pbChoice?.panel !== 'cp' || !pbDay?.hours || !prebaked?.cp) return null;
    const base = Object.fromEntries(pbDay.hours.map((b) => [b.hour, b.entries]));
    if (pbChoice.id === 'baseline') return { base, scenario: base, name: null };
    const s = pbDay.scenarios.find((x) => x.id === pbChoice.id);
    const short = prebaked.cp.scenarios.find((x) => x.id === pbChoice.id)?.short;
    if (!s || !short) return null;
    const scenario = {};
    for (const b of pbDay.hours) {
      if (!b.charged) scenario[b.hour] = b.entries;
      else if (s.hours[b.hour]) scenario[b.hour] = s.hours[b.hour].entries;
    }
    return { base, scenario, name: short.toLowerCase() };
  }, [showCongestionPricing, pbChoice, pbDay, prebaked]);

  // Frames what an edit changed: a retimed junction close up, roads to their extent.
  // A jump, not a transition: deck.gl reports a transition's frames while it renders,
  // and that setState is React's update-during-render warning.
  const flyToTargets = (fc) => {
    if (fc.junction) {
      setViewState((vs) => ({ ...vs, longitude: fc.junction.lng, latitude: fc.junction.lat, zoom: 15.5 }));
      return;
    }
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
    for (const f of fc.features) {
      for (const [x, y] of f.geometry.coordinates) {
        if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y;
      }
    }
    if (!Number.isFinite(x0)) return;
    const vp = new WebMercatorViewport({ width: window.innerWidth, height: window.innerHeight });
    const { longitude, latitude, zoom } = vp.fitBounds([[x0, y0], [x1, y1]], { padding: 160 });
    setViewState((vs) => ({ ...vs, longitude, latitude, zoom: Math.min(zoom, 16) }));
  };

  // The bands' streets in the part of the map the panel and the day strip leave free.
  const flyOverBands = (outlines) => {
    const map = document.getElementById('deckgl-overlay')?.getBoundingClientRect();
    const panel = cpPanelRef.current?.getBoundingClientRect();
    const pts = outlines.flatMap((o) => o.ring);
    if (!map || !pts.length) return;
    const xs = pts.map((pt) => pt[0]);
    const ys = pts.map((pt) => pt[1]);
    const covered = panel && panel.right > map.left && panel.right < map.right ? panel.right - map.left : 0;
    const left = map.width - covered >= 260 ? covered + 30 : 40;
    const vp = new WebMercatorViewport({ width: map.width, height: map.height });
    const { longitude, latitude, zoom } = vp.fitBounds(
      [[Math.min(...xs), Math.min(...ys)], [Math.max(...xs), Math.max(...ys)]],
      { padding: { left, right: 60, top: 60, bottom: Math.min(170, map.height / 3) } });
    setViewState((vs) => ({ ...vs, longitude, latitude, zoom: Math.min(zoom, 15) }));
  };

  // The charging zone centred in the part of the map the panel leaves free: centred
  // on the whole map, the panel covers most of it.
  const focusBesidePanel = (focus, panelEl) => {
    const map = document.getElementById('deckgl-overlay')?.getBoundingClientRect();
    const panel = panelEl?.getBoundingClientRect();
    if (!map || !panel || panel.right <= map.left || panel.right >= map.right) return focus;
    const vp = new WebMercatorViewport({ width: map.width, height: map.height,
                                         longitude: focus.lng, latitude: focus.lat, zoom: focus.zoom });
    const freeMiddle = (panel.right - map.left + map.width) / 2;
    const [lng, lat] = vp.unproject([map.width - freeMiddle, map.height / 2]);
    return { lng, lat, zoom: focus.zoom };
  };

  // The hour the pricing scenarios are read at: the map's, else the pack's own.
  const cpHour = viewHour ?? prebaked?.default_hour ?? null;

  // One choice's change on the map at `hour`; nothing to fetch for no charge or
  // for an hour the scenario has no result.
  const loadPrebakedResult = (id, against, hour) => {
    const ask = ++pbResultAsk.current;
    const isEdit = (prebaked.edits || []).some((e) => e.id === id);
    const scenario = !isEdit && (prebaked.cp?.scenarios || []).find((s) => s.id === id);
    if (!isEdit && (!scenario
        || scenarioHour(scenario, hour, prebaked.cp.toll_windows_h).kind !== 'computed')) {
      setPbResult(null);
      return;
    }
    setPbResult({ loading: true });
    fetchPrebakedComparison(activeDataset.cbsa, prebaked.run_id, id, { against, hour })
      .then((c) => { if (ask === pbResultAsk.current) setPbResult(c); })
      .catch((err) => {
        if (ask === pbResultAsk.current) setPbResult({ error: `The result could not be loaded (${err.message}).` });
      });
  };

  // `against` is what a pricing scenario is compared with; 'baseline' as the id
  // is no charge, which has nothing to compare.
  const choosePrebaked = (panel, id, against = 'baseline') => {
    if (!prebaked || !activeDataset?.cbsa) return;
    const ask = ++pbAsk.current;
    const cbsa = activeDataset.cbsa;
    const isEdit = (prebaked.edits || []).some((e) => e.id === id);
    // A pricing scenario frames the zone (or its bands) on the panel's first choice only;
    // switching scenarios leaves the map where the user put it.
    const firstInPanel = pbChoice?.panel !== panel;
    setPbChoice({ panel, id, against });
    setPbTargets(null);
    hideTooltip();
    if (isEdit) {
      fetchPrebakedTargets(cbsa, id)
        .then((t) => {
          if (ask !== pbAsk.current) return;
          setPbTargets(t);
          flyToTargets(t);
        })
        .catch((err) => console.error('Failed to load the edited roads:', err));
    } else if (!firstInPanel) {
      pbBandsFly.current = false;
    } else if (pbBandsOn && (prebaked.cp?.scenarios || []).some((s) => s.id === id && s.route_cost)) {
      pbBandsFly.current = true;
    } else if (prebaked.focus) {
      const f = focusBesidePanel(prebaked.focus, cpPanelRef.current);
      setViewState((vs) => ({ ...vs, longitude: f.lng, latitude: f.lat, zoom: f.zoom }));
    }
    loadPrebakedResult(id, against, isEdit ? prebaked.edits_hour : cpHour);
  };

  // A pricing scenario follows the hour: the same choice, read at the new hour.
  useEffect(() => {
    if (pbChoice?.panel === 'cp' && prebaked && cpHour !== null) {
      loadPrebakedResult(pbChoice.id, pbChoice.against, cpHour);
    }
  }, [cpHour]);

  // A scenario with service levels: the level is shared with the workshop pages, so a
  // change made there switches the variant chosen here.
  const pbChosen = pbChoice?.panel === 'cp' ? (prebaked?.cp?.scenarios || []).find((s) => s.id === pbChoice.id) || null : null;
  const pbLevelBase = pbChosen ? pbChosen.variant_of || pbChosen.id : null;
  const pbLevels = pbLevelBase ? packLevels(prebaked.cp.scenarios, pbLevelBase) : null;
  useEffect(() => {
    if (!pbLevels) return;
    const id = idAtLevel(prebaked.cp.scenarios, pbLevelBase, serviceLevel);
    if (id !== pbChoice.id) choosePrebaked('cp', id, pbChoice.against);
  }, [serviceLevel]);

  // Each level's line ranking, for the panel's table of levels; read once a scenario with levels is chosen.
  const pbLevelKey = showCongestionPricing && pbLevels ? pbLevels.map((l) => l.level).join(',') : null;
  const [pbRanks, setPbRanks] = useState(null); // { byLevel } of transit_rank.json, { loading } or { error }
  useEffect(() => {
    if (!pbLevelKey) return undefined;
    let cancelled = false;
    const levels = pbLevelKey.split(',').map(Number);
    setPbRanks((cur) => (cur?.byLevel && cur.key === pbLevelKey ? cur : { key: pbLevelKey, loading: true }));
    Promise.all(levels.map((level) => fetchStaticJson(rankUrl(level))))
      .then((docs) => {
        if (!cancelled) setPbRanks({ key: pbLevelKey, byLevel: Object.fromEntries(levels.map((l, i) => [l, docs[i]])) });
      })
      .catch((err) => {
        if (!cancelled) {
          setPbRanks({ key: pbLevelKey, error: `The service levels could not be loaded (${err.message}). Pick another scenario, then this one again, to retry.` });
        }
      });
    return () => { cancelled = true; };
  }, [pbLevelKey]);

  // A route-cost scenario's corridors on the map: each group's roads with the
  // scenario's V/C at the hour, while its toggle is on.
  const pbRouteCost = showCongestionPricing && pbChoice?.panel === 'cp'
    ? (prebaked?.cp?.scenarios || []).find((s) => s.id === pbChoice.id && s.route_cost) || null : null;
  const bandsShown = !!pbRouteCost && cpHour !== null
    && scenarioHour(pbRouteCost, cpHour, prebaked.cp.toll_windows_h).kind === 'computed';
  const pbCbsa = activeDataset?.cbsa;
  const pbRunId = prebaked?.run_id;
  const pbDefaultHour = prebaked?.default_hour ?? null;
  // The outlines need only the streets' shape, the same in every run and hour: read once
  // from the baseline, not again when the hour changes.
  useEffect(() => {
    const ask = ++pbBandsAsk.current;
    if (!pbRouteCost || !pbBandsOn || !bandsShown) {
      pbBandsFly.current = false;
      setPbBands(null);
      return;
    }
    const corridors = pbRouteCost.route_cost.corridors;
    setPbBands({ loading: true });
    fetchEdgeTraffic(pbCbsa, pbRunId, 'baseline', pbDefaultHour, treatedEdgeIds(corridors))
      .then((r) => {
        if (ask !== pbBandsAsk.current) return;
        const paths = Object.fromEntries(r.features.map((f) => [f.properties.edgeId, f.geometry.coordinates]));
        const outlines = bandOutlines(corridors, paths);
        setPbBands({ outlines });
        if (pbBandsFly.current) {
          pbBandsFly.current = false;
          flyOverBands(outlines);
        }
      })
      .catch((err) => {
        if (ask !== pbBandsAsk.current) return;
        pbBandsFly.current = false;
        setPbBands({ error: `The bands could not be loaded (${err.message}).` });
      });
  }, [pbRouteCost, pbBandsOn, bandsShown, pbCbsa, pbRunId, pbDefaultHour]);

  const clearPrebaked = () => {
    pbAsk.current += 1;
    pbResultAsk.current += 1;
    setPbChoice(null);
    setPbResult(null);
    setPbTargets(null);
    hideTooltip();
  };

  // A result belongs to the panel that asked for it; closing that panel drops it.
  useEffect(() => {
    if (!pbChoice) return;
    const open = { cp: showCongestionPricing, sz: showSchoolZones, ne: showNetworkEditor }[pbChoice.panel];
    if (!open) clearPrebaked();
  }, [showCongestionPricing, showSchoolZones, showNetworkEditor, pbChoice]);

  // The editor highlights every edit on the map so one can be picked there too.
  useEffect(() => {
    if (!showNetworkEditor || !prebakedOnly || !prebaked || pbCandidates) return undefined;
    let cancelled = false;
    Promise.all(pbNeEdits.map((e) => fetchPrebakedTargets(activeDataset.cbsa, e.id)))
      .then((all) => { if (!cancelled) setPbCandidates(all); })
      .catch((err) => console.error('Failed to load the editable roads:', err));
    return () => { cancelled = true; };
  }, [showNetworkEditor, prebakedOnly, prebaked, pbCandidates, pbNeEdits]);

  const handleNeDragStart = (e) => {
    if (e.target.closest('input') || e.target.closest('button') || e.target.closest('select')) return;
    setIsNeDragging(true);
    neDragStartRef.current = { x: e.clientX - nePanelPosition.x, y: e.clientY - nePanelPosition.y };
  };
  const handleNeDragMove = (e) => {
    if (!isNeDragging) return;
    setNePanelPosition(onMap(nePanelRef.current,
      { x: e.clientX - neDragStartRef.current.x, y: e.clientY - neDragStartRef.current.y }));
  };
  const handleNeDragEnd = () => setIsNeDragging(false);
  useEffect(() => {
    if (isNeDragging) {
      window.addEventListener('mousemove', handleNeDragMove);
      window.addEventListener('mouseup', handleNeDragEnd);
    }
    return () => {
      window.removeEventListener('mousemove', handleNeDragMove);
      window.removeEventListener('mouseup', handleNeDragEnd);
    };
  }, [isNeDragging]);
  useKeepOnMap(nePanelRef, setNePanelPosition, showNetworkEditor);

  const handleMacroDragStart = (e) => {
    if (e.target.closest('input') || e.target.closest('button') || e.target.closest('select')) return;
    setIsMacroDragging(true);
    macroDragStartRef.current = {
      x: e.clientX - macroPanelPosition.x,
      y: e.clientY - macroPanelPosition.y
    };
  };

  const handleMacroDragMove = (e) => {
    if (!isMacroDragging) return;
    setMacroPanelPosition({
      x: e.clientX - macroDragStartRef.current.x,
      y: Math.max(0, e.clientY - macroDragStartRef.current.y)
    });
  };

  const handleMacroDragEnd = () => {
    setIsMacroDragging(false);
  };

  useEffect(() => {
    if (isMacroDragging) {
      window.addEventListener('mousemove', handleMacroDragMove);
      window.addEventListener('mouseup', handleMacroDragEnd);
    }
    return () => {
      window.removeEventListener('mousemove', handleMacroDragMove);
      window.removeEventListener('mouseup', handleMacroDragEnd);
    };
  }, [isMacroDragging]);

  const handleTdDragStart = (e) => {
    if (e.target.closest('input') || e.target.closest('button')) return;
    setIsTdDragging(true);
    tdDragStartRef.current = {
      x: e.clientX - tdPanelPosition.x,
      y: e.clientY - tdPanelPosition.y
    };
  };

  const handleTdDragMove = (e) => {
    if (!isTdDragging) return;
    setTdPanelPosition(onMap(tdPanelRef.current, {
      x: e.clientX - tdDragStartRef.current.x,
      y: e.clientY - tdDragStartRef.current.y,
    }));
  };

  const handleTdDragEnd = () => {
    setIsTdDragging(false);
  };

  useEffect(() => {
    if (isTdDragging) {
      window.addEventListener('mousemove', handleTdDragMove);
      window.addEventListener('mouseup', handleTdDragEnd);
    }
    return () => {
      window.removeEventListener('mousemove', handleTdDragMove);
      window.removeEventListener('mouseup', handleTdDragEnd);
    };
  }, [isTdDragging]);
  useKeepOnMap(tdPanelRef, setTdPanelPosition, showTrafficPanel);

  const handleSzDragStart = (e) => {
    if (e.target.closest('input') || e.target.closest('button')) return;
    setIsSzDragging(true);
    szDragStartRef.current = {
      x: e.clientX - szPanelPosition.x,
      y: e.clientY - szPanelPosition.y
    };
  };

  const handleSzDragMove = (e) => {
    if (!isSzDragging) return;
    setSzPanelPosition(onMap(szPanelRef.current, {
      x: e.clientX - szDragStartRef.current.x,
      y: e.clientY - szDragStartRef.current.y,
    }));
  };

  const handleSzDragEnd = () => {
    setIsSzDragging(false);
  };

  useEffect(() => {
    if (isSzDragging) {
      window.addEventListener('mousemove', handleSzDragMove);
      window.addEventListener('mouseup', handleSzDragEnd);
    }
    return () => {
      window.removeEventListener('mousemove', handleSzDragMove);
      window.removeEventListener('mouseup', handleSzDragEnd);
    };
  }, [isSzDragging]);
  useKeepOnMap(szPanelRef, setSzPanelPosition, showSchoolZones);

  const handleCpDragStart = (e) => {
    if (e.target.closest('input') || e.target.closest('button')) return;
    setIsCpDragging(true);
    cpDragStartRef.current = {
      x: e.clientX - cpPanelPosition.x,
      y: e.clientY - cpPanelPosition.y
    };
  };

  const handleCpDragMove = (e) => {
    if (!isCpDragging) return;
    setCpPanelPosition(onMap(cpPanelRef.current, {
      x: e.clientX - cpDragStartRef.current.x,
      y: e.clientY - cpDragStartRef.current.y,
    }));
  };

  const handleCpDragEnd = () => {
    setIsCpDragging(false);
  };

  useEffect(() => {
    if (isCpDragging) {
      window.addEventListener('mousemove', handleCpDragMove);
      window.addEventListener('mouseup', handleCpDragEnd);
    }
    return () => {
      window.removeEventListener('mousemove', handleCpDragMove);
      window.removeEventListener('mouseup', handleCpDragEnd);
    };
  }, [isCpDragging]);
  useKeepOnMap(cpPanelRef, setCpPanelPosition, showCongestionPricing);

  // Ray-casting point-in-polygon test
  const pointInPolygon = (point, polygon) => {
    const [x, y] = point;
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const [xi, yi] = polygon[i];
      const [xj, yj] = polygon[j];
      if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) {
        inside = !inside;
      }
    }
    return inside;
  };

  // Close the current polygon being drawn
  const closeMacroPolygon = () => {
    const closed = [...macroDrawingVertices, macroDrawingVertices[0]];
    if (macroDrawingMode === 'analysis-area') {
      setMacroAnalysisArea(closed);
    } else if (macroDrawingMode === 'crz') {
      setMacroCrzArea(closed);
    } else if (macroDrawingMode === 'traffic-area') {
      setTdArea(closed);
    }
    setMacroDrawingVertices([]);
    setMacroDrawingMode('none');
  };

  // Handle macro analysis map clicks (polygon drawing)
  const handleMacroMapClick = (info) => {
    if (macroDrawingMode === 'none') return false;
    if (!info.coordinate) return false;

    const [lng, lat] = info.coordinate;

    // Check if clicking near first vertex to close (requires >= 3 vertices)
    if (macroDrawingVertices.length >= 3) {
      const [firstLng, firstLat] = macroDrawingVertices[0];
      const dist = Math.sqrt((lng - firstLng) ** 2 + (lat - firstLat) ** 2);
      if (dist < 0.001) {
        closeMacroPolygon();
        return true;
      }
    }

    // Add vertex
    setMacroDrawingVertices(prev => [...prev, [lng, lat]]);
    return true;
  };

  /** Hide the hover tooltip. deck.gl only clears it via an onHover firing with a null
   *  object, which never happens when the cursor moves onto a panel. */
  const hideTooltip = () => {
    const t = document.getElementById('tooltip');
    if (t) t.style.display = 'none';
  };

  useEffect(() => {
    const onOver = (e) => {
      if (!(e.target instanceof HTMLCanvasElement)) hideTooltip();
    };
    document.addEventListener('mouseover', onOver);
    return () => document.removeEventListener('mouseover', onOver);
  }, []);

  // Get macro analysis polygon layers
  const getMacroLayers = () => {
    const macroLayers = [];

    // Traffic Density's own area, drawn and owned by its panel.
    if (showTrafficPanel && tdArea) {
      macroLayers.push(new PolygonLayer({
        id: 'macro-analysis-area',
        data: [{ polygon: tdArea }],
        getPolygon: d => d.polygon,
        getFillColor: [124, 198, 68, 30],
        getLineColor: [124, 198, 68, 220],
        getLineWidth: 2,
        lineWidthMinPixels: 2,
        filled: true,
        stroked: true,
      }));
    }

    if (showCongestionPricing && macroAnalysisArea) {
      macroLayers.push(new PolygonLayer({
        id: 'macro-analysis-area',
        data: [{ polygon: macroAnalysisArea }],
        getPolygon: d => d.polygon,
        getFillColor: [59, 130, 246, 30],
        getLineColor: [59, 130, 246, 200],
        getLineWidth: 2,
        lineWidthMinPixels: 2,
        filled: true,
        stroked: true,
      }));
    }

    // Completed CRZ polygon
    if (showCongestionPricing && macroCrzArea) {
      macroLayers.push(new PolygonLayer({
        id: 'macro-crz-area',
        data: [{ polygon: macroCrzArea }],
        getPolygon: d => d.polygon,
        getFillColor: [245, 158, 11, 40],
        getLineColor: [245, 158, 11, 220],
        getLineWidth: 2,
        lineWidthMinPixels: 2,
        filled: true,
        stroked: true,
      }));
    }

    // In-progress drawing: vertices + connecting lines
    if (macroDrawingVertices.length > 0) {
      const color = macroDrawingMode === 'crz' ? [245, 158, 11, 255]
        : macroDrawingMode === 'traffic-area' ? [124, 198, 68, 255]
        : [59, 130, 246, 255];

      // Vertex dots
      macroLayers.push(new ScatterplotLayer({
        id: 'macro-drawing-vertices',
        data: macroDrawingVertices.map(v => ({ position: v })),
        getPosition: d => d.position,
        getFillColor: color,
        getRadius: 6,
        radiusMinPixels: 6,
        radiusMaxPixels: 10,
      }));

      // Close hint: larger halo on first vertex when >= 3 points
      if (macroDrawingVertices.length >= 3) {
        macroLayers.push(new ScatterplotLayer({
          id: 'macro-close-hint',
          data: [{ position: macroDrawingVertices[0] }],
          getPosition: d => d.position,
          getFillColor: [...color.slice(0, 3), 100],
          getRadius: 14,
          radiusMinPixels: 14,
          radiusMaxPixels: 18,
          stroked: true,
          getLineColor: color,
          lineWidthMinPixels: 2,
        }));
      }

      // Connecting lines
      if (macroDrawingVertices.length >= 2) {
        macroLayers.push(new PathLayer({
          id: 'macro-drawing-lines',
          data: [{ path: macroDrawingVertices }],
          getPath: d => d.path,
          getColor: [...color.slice(0, 3), 150],
          getWidth: 2,
          widthMinPixels: 2,
        }));
      }
    }

    return macroLayers;
  };

  // Close macro analysis
  const handleCloseMacroAnalysis = () => {
    setMacroScenario('plain');
    setMacroDrawingMode('none');
    setMacroDrawingVertices([]);
    setMacroAnalysisArea(null);
    setMacroCrzArea(null);
    setMacroResultData(null);
    setShowMacroResultModal(false);
    setMacroParams({
      dayOfWeek: 'weekday', timeStart: 7, timeEnd: 19,
      vtts: 21.10, schoolStart: '08:00', schoolEnd: '17:00', zoneRadius: 200,
    });
    if (onCloseMacroAnalysis) onCloseMacroAnalysis();
  };

  // Check if CRZ polygon is fully inside analysis area polygon
  const isCrzOutside = macroScenario === 'congestion-pricing' && macroCrzArea && macroAnalysisArea && (
    !macroCrzArea.slice(0, -1).every(vertex => pointInPolygon(vertex, macroAnalysisArea))
  );

  /** Scenario controls (parameters, draw tool, Run, result) for a produced run.
   *  Rendered inside the School Zones and Congestion Pricing panels. */
  /** The paper's static Manhattan comparison report (level 1/2/3). */
  const openBakedReport = async (scenario) => {
    try {
      const res = await fetch(`/data/case-study/${scenario}.json`);
      if (!res.ok) throw new Error(`report ${res.status}`);
      localStorage.setItem('macroAnalysisResult', JSON.stringify(await res.json()));
      localStorage.setItem('macroAnalysisScenario', scenario);
      window.open(`${window.location.origin}/home?view=analysis-result`, '_blank');
    } catch (e) {
      console.error('openBakedReport:', e);
    }
  };

  const renderScenarioControls = (scenario) => {
    if (!liveSim || activeDataset?.kind !== 'run') return null;
    const isZone = scenario === 'congestion-pricing';
    const fmt = (v) => (v == null ? '—' : `${v > 0 ? '+' : ''}${v}%`);
    const r = macroRunResult && macroRunResult.scenario === scenario ? macroRunResult : null;

    return (
      <div className="scenario-controls">
        <div className="scenario-controls-head">
          Simulate on {String(activeDataset.label).split(' \u2014 ')[0]},
          {' '}{String(activeDataset.hour).padStart(2, '0')}:00–{String((activeDataset.hour + 1) % 24).padStart(2, '0')}:00
        </div>

        {isZone ? (
          <>
            <div className="scenario-field">
              <label>Charging zone</label>
              {macroCrzArea ? (
                <div className="macro-area-indicator">
                  <span className="macro-area-coords">Zone drawn ({macroCrzArea.length - 1} vertices)</span>
                  <button
                    className="macro-area-clear"
                    onClick={() => { setMacroCrzArea(null); clearScenarioResult(); }}
                    title="Clear"
                  >
                    <FaTimes size={10} />
                  </button>
                </div>
              ) : (
                <button
                  className={`macro-draw-btn crz ${macroDrawingMode === 'crz' ? 'drawing' : ''}`}
                  onClick={() => {
                    setMacroDrawingMode(macroDrawingMode === 'crz' ? 'none' : 'crz');
                    setMacroDrawingVertices([]);
                  }}
                >
                  <FaCrosshairs size={12} />
                  {macroDrawingMode === 'crz' ? `Drawing... (${macroDrawingVertices.length} pts)` : 'Draw zone on map'}
                </button>
              )}
            </div>
            {macroDrawingMode === 'crz' && macroDrawingVertices.length > 0 && (
              <div className="macro-drawing-actions">
                <button onClick={() => setMacroDrawingVertices(prev => prev.slice(0, -1))}>
                  <FaUndo size={10} /> Undo
                </button>
                {macroDrawingVertices.length >= 3 && (
                  <button onClick={closeMacroPolygon}><FaCheck size={10} /> Close</button>
                )}
              </div>
            )}
            <div className="scenario-field">
              <label>VTTS ($/hr)</label>
              <input
                type="number" step="0.1" min={0.1}
                value={macroParams.vtts}
                onChange={e => setMacroParams(p => ({ ...p, vtts: e.target.value }))}
              />
            </div>
          </>
        ) : (
          <>
            <div className="scenario-field">
              <label>Zone radius (m)</label>
              <input
                type="number" min={10} max={2000} step={10}
                value={szParams.radiusM}
                onChange={e => setSzParams(p => ({ ...p, radiusM: e.target.value }))}
              />
            </div>
            <div className="scenario-field">
              <label>Speed limit (mph)</label>
              <input
                type="number" min={5} max={70}
                value={szParams.speedMph}
                onChange={e => setSzParams(p => ({ ...p, speedMph: e.target.value }))}
              />
            </div>
          </>
        )}

        {(() => {
          const est = estimateCost(
            costModel, isZone ? { zone: true } : { policy: { kind: 'school_zone' } });
          return est && !macroRunning && !r ? (
            <div className="macro-run-cost-est">
              Est. ~{est.seconds}s · ~{formatUsd(est.usd)} of compute
            </div>
          ) : null;
        })()}

        <button
          className="macro-run-btn"
          disabled={macroRunning || (isZone && !macroCrzArea)}
          onClick={() => runScenarioLive(scenario)}
        >
          <FaPlay size={12} />
          {macroRunning ? 'Running the simulation…' : 'Run simulation'}
        </button>

        {r && (
          <div className="macro-run-result">
            {r.error ? (
              <div className="macro-run-error">{r.error}</div>
            ) : (
              <>
                <div className="macro-run-title">
                  {isZone
                    ? `${r.data.zone.edges} segments priced · +${Math.round(r.data.zone.penaltyS)} s each`
                    : `This run: ${r.data.zone.affectedEdges} segments capped · `
                      + `${r.data.zone.affectedKm} km · `
                      + `${r.data.zone.schoolsInRange}/${r.data.zone.schools} schools reached`}
                </div>
                <div className="macro-run-grid">
                  <div><span>{fmt(r.data.totals.inZoneVehicles.pct)}</span>vehicles in zone</div>
                  <div><span>{fmt(r.data.totals.outsideVehicles.pct)}</span>vehicles outside</div>
                  <div><span>{r.data.totals.congestedEdges.b}</span>congested (was {r.data.totals.congestedEdges.a})</div>
                  <div><span>{r.data.elapsedS}s</span>simulation time</div>
                  {r.data.cost && (
                    <div><span>{formatUsd(r.data.cost.usd)}</span>compute cost</div>
                  )}
                </div>
                {r.data.cost && (
                  <div className="macro-run-cost-note">
                    Marginal compute only — {r.data.cost.elapsed_s}s on{' '}
                    {r.data.cost.rate.instance} at ${r.data.cost.rate.usd_per_hour}/hr.
                    Excludes idle time, storage and fixed infrastructure.
                  </div>
                )}
                <div className="run-legend">
                  <div className="run-legend-title">V/C change on the map (baseline → this run)</div>
                  {VC_DELTA_LEGEND.map((b) => (
                    <div className="run-legend-row" key={b.label}>
                      <span
                        className="run-legend-swatch"
                        style={{ background: `rgb(${b.color.join(',')})` }}
                      />
                      <span>{b.label}</span>
                    </div>
                  ))}
                </div>
                <div className="macro-run-note">
                  {isZone
                    ? `Charged on every segment inside the zone, converted to time at $${r.data.zone.vtts}/h. Read this as drivers minimising time in the zone, not toll compliance.`
                    : `Capped from a mean of ${kmhToMph(r.data.zone.meanSpeedBeforeKmh)} mph. V/C falls inside the zones because traffic leaves them — the cost lands on travel time elsewhere.`}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  // Both scenarios go through the real chain: the drawn zone as a weight file
  // (~7 s), school zones as a policy the service applies to its own network (~14 s).
  useEffect(() => {
    if (!macroRunResult) return;
    const open = macroRunResult.scenario === 'congestion-pricing'
      ? showCongestionPricing
      : showSchoolZones;
    if (!open) { setMacroRunResult(null); setMacroDeltaFeatures(null); hideTooltip(); }
  }, [showCongestionPricing, showSchoolZones, macroRunResult]);

  /** Switch Traffic Density selection mode, dropping the other mode's state. */
  const selectTdMode = (mode) => {
    setTdMode(mode);
    setMacroDrawingMode('none');
    setMacroDrawingVertices([]);
    if (mode === 'area') {
      setSelectedHexagons(new Set());
      hexIdToCoordinates.current.clear();
      setShowHexagons(false);
    } else {
      setTdArea(null);
      setShowHexagons(true);
    }
    if (onTrafficResult) onTrafficResult(null);
  };

  const closeTrafficPanel = () => {
    setTdMode(null);
    setTdArea(null);
    setShowHexagons(false);
    setSelectedHexagons(new Set());
    hexIdToCoordinates.current.clear();
    setMacroDrawingMode('none');
    setMacroDrawingVertices([]);
    hideTooltip();
    if (onTrafficResult) onTrafficResult(null);
    if (onCloseTrafficPanel) onCloseTrafficPanel();
  };

  const runTrafficDensity = () => {
    if (!onRunTrafficDensity) return;
    setDimension(tdMode === 'hex' ? 'hexagon' : 'link');
    if (tdMode === 'area' && tdArea) {
      onRunTrafficDensity({ mode: 'area', rings: { area: tdArea } });
    } else if (tdMode === 'hex') {
      const rings = {};
      for (const [id, coords] of hexIdToCoordinates.current) rings[id] = coords[0];
      if (Object.keys(rings).length) onRunTrafficDensity({ mode: 'hex', rings });
    }
  };

  /** Drop a scenario result and everything drawn for it. */
  const clearScenarioResult = () => {
    setMacroRunResult(null);
    setMacroDeltaFeatures(null);
    hideTooltip();
  };

  const runScenarioLive = async (scenario) => {
    setMacroRunning(true);
    setMacroRunResult(null);
    setMacroDeltaFeatures(null);
    try {
      const out = await runScenario(activeDataset, scenario === 'congestion-pricing'
        ? { scenario, polygon: macroCrzArea, vtts: Number(macroParams.vtts) || undefined }
        : { scenario, radiusM: Number(szParams.radiusM), speedMph: Number(szParams.speedMph) });
      setMacroRunResult({ scenario, data: out });
      setMacroDeltaFeatures(out.features);
    } catch (e) {
      console.error('scenario run failed:', e);
      setMacroRunResult({ scenario, error: String(e.message || e) });
    } finally {
      setMacroRunning(false);
    }
  };

  const handleRunMacroAnalysis = async () => {
    const config = {
      scenario: macroScenario,
      analysisArea: macroAnalysisArea,
      ...(macroScenario === 'congestion-pricing' && { crz: macroCrzArea }),
      dayOfWeek: macroParams.dayOfWeek,
      timeRange: { start: macroParams.timeStart, end: macroParams.timeEnd },
      ...(macroScenario === 'congestion-pricing' && { vtts: macroParams.vtts }),
      ...(macroScenario === 'school-zones' && {
        schoolStart: macroParams.schoolStart,
        schoolEnd: macroParams.schoolEnd,
        zoneRadius: macroParams.zoneRadius,
      }),
    };
    console.log('=== Macro Analysis Config ===');
    console.log(JSON.stringify(config, null, 2));

    // Load mock data based on scenario
    const dataFiles = {
      'plain': '/data/case-study/base.json',
      'congestion-pricing': '/data/case-study/congestion-pricing.json',
      'school-zones': '/data/case-study/school-zones.json',
    };
    const dataFile = dataFiles[macroScenario];

    try {
      const res = await fetch(dataFile);
      if (!res.ok) throw new Error(`Failed to load: ${res.status}`);
      const json = await res.json();
      setMacroResultData(json);
      setShowMacroResultModal(true);
    } catch (err) {
      console.error('Failed to load analysis data:', err);
    }
  };

  // Open full analysis in new tab
  const handleOpenFullAnalysis = () => {
    localStorage.setItem('macroAnalysisResult', JSON.stringify(macroResultData));
    localStorage.setItem('macroAnalysisScenario', macroScenario);
    window.open(`${window.location.origin}/home?view=analysis-result`, '_blank');
  };

  // --- School Zones ---

  // Generate circle polygon from center + radius
  const generateCirclePolygon = (centerLon, centerLat, radiusMeters, numPoints = 64) => {
    const coords = [];
    const earthRadius = 6371000;
    for (let i = 0; i <= numPoints; i++) {
      const angle = (i / numPoints) * 2 * Math.PI;
      const dLat = (radiusMeters / earthRadius) * Math.cos(angle) * (180 / Math.PI);
      const dLon = (radiusMeters / (earthRadius * Math.cos(centerLat * Math.PI / 180))) * Math.sin(angle) * (180 / Math.PI);
      coords.push([centerLon + dLon, centerLat + dLat]);
    }
    return coords;
  };

  // Generate dashed circle border as array of short path segments
  const generateDashedCircle = (centerLon, centerLat, radiusMeters, numPoints = 64, dashCount = 16) => {
    const allPoints = generateCirclePolygon(centerLon, centerLat, radiusMeters, numPoints);
    const pointsPerSegment = Math.floor(numPoints / (dashCount * 2)); // half dash, half gap
    const dashes = [];
    for (let i = 0; i < dashCount; i++) {
      const start = i * pointsPerSegment * 2;
      const end = start + pointsPerSegment;
      if (end <= allPoints.length) {
        dashes.push(allPoints.slice(start, end + 1));
      }
    }
    return dashes;
  };

  // V/C ratio change color scale (absolute delta, not percentage)
  // Negative delta = V/C decreased = congestion relieved (green)
  // Positive delta = V/C increased = congestion worsened (red)
  const getVcChangeColor = (deltaVc) => {
    if (deltaVc < -0.1)  return [0, 100, 0, 200];       // Dark green: relieved significantly
    if (deltaVc < -0.01) return [144, 238, 144, 200];    // Light green: relieved moderately
    if (deltaVc <= 0.01) return [150, 150, 150, 180];    // Gray: minimal change
    if (deltaVc <= 0.1)  return [255, 160, 160, 200];    // Light red: worsened moderately
    return [139, 0, 0, 220];                              // Dark red: worsened significantly
  };

  // Every layer this panel owns. Must stay the single list — the update effect filters
  // old layers out by it.
  const SZ_LAYER_IDS = ['sz-school-dots', 'sz-zone-circles', 'sz-zone-dashes',
                        'sz-affected-roads', 'sz-speed-heatmap', 'sz-school-names'];

  // Build school zone layers
  const getSchoolZoneLayers = () => {
    if (!szData || !showSchoolZones) return [];
    const szLayers = [];

    // Zone circles — fill only (no stroke)
    const circleData = szData.schools.map(s => ({
      polygon: generateCirclePolygon(s.lon, s.lat, 200),
      name: s.name,
    }));
    szLayers.push(new PolygonLayer({
      id: 'sz-zone-circles',
      data: circleData,
      getPolygon: d => d.polygon,
      getFillColor: [255, 182, 193, 77],
      filled: true,
      stroked: false,
    }));

    // Zone circles — dashed dark red border
    const dashSegments = szData.schools.flatMap(s =>
      generateDashedCircle(s.lon, s.lat, 200).map(path => ({ path }))
    );
    szLayers.push(new PathLayer({
      id: 'sz-zone-dashes',
      data: dashSegments,
      getPath: d => d.path,
      getColor: [139, 0, 0, 200],
      getWidth: 4,
      widthMinPixels: 2,
    }));

    // School dots (dark blue)
    szLayers.push(new ScatterplotLayer({
      id: 'sz-school-dots',
      data: szData.schools,
      getPosition: d => [d.lon, d.lat],
      getFillColor: [0, 0, 139, 255],
      getRadius: 60,
      radiusMinPixels: 4,
      radiusMaxPixels: 8,
      pickable: true,
      onHover: ({ object, x, y }) => {
        const tooltip = document.getElementById('tooltip');
        if (object) {
          const canvas = document.querySelector('canvas');
          const rect = canvas ? canvas.getBoundingClientRect() : { left: 0, top: 0 };
          tooltip.style.display = 'block';
          tooltip.style.left = `${rect.left + x + 10}px`;
          tooltip.style.top = `${rect.top + y + 10}px`;
          tooltip.innerHTML = `<b>${object.name}</b>`;
        } else {
          tooltip.style.display = 'none';
        }
      }
    }));

    // Toggle A: Affected roads (red)
    if (szShowAffected) {
      szLayers.push(new PathLayer({
        id: 'sz-affected-roads',
        data: szData.affectedEdges,
        getPath: d => d.path,
        getColor: [220, 20, 20, 200],
        getWidth: 3,
        widthMinPixels: 2,
      }));
    }

    // Toggle B: V/C ratio change heatmap
    if (szShowHeatmap) {
      szLayers.push(new PathLayer({
        id: 'sz-speed-heatmap',
        data: szData.vcChange,
        getPath: d => d.path,
        getColor: d => getVcChangeColor(d.delta_vc),
        getWidth: 3,
        widthMinPixels: 2,
        pickable: true,
        onHover: ({ object, x, y }) => {
          const tooltip = document.getElementById('tooltip');
          if (object) {
            const canvas = document.querySelector('canvas');
            const rect = canvas ? canvas.getBoundingClientRect() : { left: 0, top: 0 };
            tooltip.style.display = 'block';
            tooltip.style.left = `${rect.left + x + 10}px`;
            tooltip.style.top = `${rect.top + y + 10}px`;
            tooltip.innerHTML = `<b>V/C Change:</b> ${object.delta_vc > 0 ? '+' : ''}${object.delta_vc.toFixed(4)}<br/><b>Plain V/C:</b> ${object.plain_vc.toFixed(4)}<br/><b>SZ V/C:</b> ${object.sz_vc.toFixed(4)}`;
          } else {
            tooltip.style.display = 'none';
          }
        }
      }));
    }

    // Toggle C: School names text labels
    if (szShowNames) {
      szLayers.push(new TextLayer({
        id: 'sz-school-names',
        data: szData.schools,
        getPosition: d => [d.lon, d.lat],
        getText: d => d.name,
        getSize: 11,
        getColor: [255, 255, 255, 220],
        getPixelOffset: [0, -14],
        fontFamily: 'Arial, sans-serif',
        fontWeight: 'bold',
        background: true,
        getBackgroundColor: [0, 0, 0, 160],
        backgroundPadding: [3, 2, 3, 2],
        getBorderRadius: 3,
        sizeUnits: 'pixels',
      }));
    }

    return szLayers;
  };

  // The /data School Zones and Congestion Pricing artefacts are Manhattan-only, so both
  // panels are gated on the dataset.
  const caseStudyRegion = !activeDataset || activeDataset.kind === 'mock';

  // Two sources, one shape {schools, affectedEdges, vcChange}: baked artefacts for
  // Manhattan, the region's own school list for a produced run. A produced region
  // has no stored school-zone run any more -- the policy is run live below -- so
  // the two overlays that came from one are Manhattan-only.
  useEffect(() => {
    if (!showSchoolZones) {
      setLayers(prev => prev.filter(l => l && !SZ_LAYER_IDS.includes(l.id)));
      return;
    }
    if (szData) return;

    setSzLoading(true);

    // The service does not hold a metro offered only as computed scenarios; its
    // schools come with them.
    if (prebakedOnly) {
      if (!prebaked) return;
      setSzData({ schools: prebaked.schools || [], affectedEdges: [], vcChange: [] });
      setSzLoading(false);
      return;
    }

    const load = caseStudyRegion
      ? Promise.all([
          fetch('/data/school-zones/schools.json').then(r => r.json()),
          fetch('/data/school-zones/sz_affected_edges.json').then(r => r.json()),
          fetch('/data/school-zones/sz_vc_change.json').then(r => r.json()),
        ]).then(([schools, affectedEdges, vcChange]) => ({
          schools, affectedEdges, vcChange,
        }))
      : fetchSchools(activeDataset?.cbsa).then((schools) => ({
          schools, affectedEdges: [], vcChange: [],
        }));

    load.then((d) => { setSzData(d); setSzLoading(false); })
        .catch(err => { console.error('Failed to load school zones data:', err); setSzLoading(false); });
  }, [showSchoolZones, caseStudyRegion, activeDataset, prebakedOnly, prebaked]);

  // Comparison result layer: per-edge V/C change from a real run.
  useEffect(() => {
    const id = 'macro-delta';
    const owner = macroRunResult && !macroRunResult.error ? macroRunResult.scenario : null;
    const ownerOpen =
      (owner === 'congestion-pricing' && showCongestionPricing) ||
      (owner === 'school-zones' && showSchoolZones);
    if (!ownerOpen || !macroDeltaFeatures || macroDeltaFeatures.length === 0) {
      setLayers(prev => prev.filter(l => l && l.id !== id));
      return;
    }
    const layer = new PathLayer({
      id,
      data: macroDeltaFeatures,
      getPath: f => f.geometry.coordinates,
      getColor: f => vcDeltaColor(f.properties.deltaVc, 220),
      getWidth: 5,
      widthMinPixels: 2,
      pickable: true,
      onHover: ({ object, x, y }) => {
        const tooltip = document.getElementById('tooltip');
        if (object) {
          const pr = object.properties;
          const canvas = document.querySelector('canvas');
          const rect = canvas ? canvas.getBoundingClientRect() : { left: 0, top: 0 };
          tooltip.style.display = 'block';
          tooltip.style.left = `${rect.left + x + 10}px`;
          tooltip.style.top = `${rect.top + y + 10}px`;
          tooltip.innerHTML = `<b>${pr.name || pr.edgeId}</b><br/>
            <b>V/C:</b> ${Number(pr.vcA).toFixed(3)} &rarr; ${pr.vcB === null ? 'closed' : Number(pr.vcB).toFixed(3)}<br/>
            <b>&Delta;:</b> ${pr.deltaVc === null ? 'n/a' : Number(pr.deltaVc).toFixed(3)}` +
            (pr.inZone ? '<br/><i>inside the priced zone</i>' : '');
        } else {
          tooltip.style.display = 'none';
        }
      },
    });
    setLayers(prev => [...prev.filter(l => l && l.id !== id), layer]);
  }, [showCongestionPricing, showSchoolZones, macroDeltaFeatures, macroRunResult]);

  // Scenarios computed ahead: the charging zone, the edits the editor offers, the
  // chosen scenario's edited roads and V/C change, and a route-cost scenario's bands.
  // Drawn in that order, except the outlines (OVERLAY_IDS), which go over everything.
  const PB_LAYER_IDS = ['pb-cordon-fill', 'pb-cordon-line', 'pb-bands-fill', 'pb-candidates',
                        'pb-candidate-junctions', 'pb-targets', 'pb-delta', 'pb-bands-outline', 'pb-junction'];
  useEffect(() => {
    const out = [];
    const cordon = showCongestionPricing && prebaked?.cp?.cordon?.geometry;
    if (cordon) {
      const polys = cordon.type === 'MultiPolygon' ? cordon.coordinates : [cordon.coordinates];
      out.push(new PolygonLayer({
        id: 'pb-cordon-fill', data: polys, getPolygon: (p) => p,
        getFillColor: [255, 255, 255, 18], filled: true, stroked: false,
      }));
      // White: no V/C grade or change class is white, so the boundary never reads as a road.
      out.push(new PathLayer({
        id: 'pb-cordon-line', data: polys.map((p) => p[0]), getPath: (ring) => ring,
        getColor: [255, 255, 255, 230], getWidth: 5, widthMinPixels: 3,
      }));
    }
    const chosen = pbChoice
      && { cp: showCongestionPricing, sz: showSchoolZones, ne: showNetworkEditor }[pbChoice.panel]
      ? pbChoice : null;
    // The fill goes under the roads, so it tints the ground and not the V/C colours.
    if (chosen && pbBands?.outlines?.length) {
      out.push(new PolygonLayer({
        id: 'pb-bands-fill', data: pbBands.outlines, getPolygon: (o) => o.ring,
        getFillColor: [...BAND_OUTLINE, 36], filled: true, stroked: false,
      }));
    }
    if (showNetworkEditor && prebakedOnly && pbCandidates) {
      const pickedId = chosen?.panel === 'ne' ? chosen.id : null;
      const roads = pbCandidates.filter((c) => c.kind === 'road')
        .flatMap((c) => c.features.map((f) => ({ path: f.geometry.coordinates, editId: c.id })));
      const junctions = pbCandidates.filter((c) => c.kind === 'signal' && c.junction)
        .map((c) => ({ position: [c.junction.lng, c.junction.lat], editId: c.id }));
      const pick = ({ object }) => {
        if (!object) return false;
        choosePrebaked('ne', object.editId);
        return true;
      };
      out.push(new PathLayer({
        id: 'pb-candidates', data: roads, getPath: (d) => d.path,
        getColor: (d) => (d.editId === pickedId ? [56, 189, 248, 70] : [56, 189, 248, 190]),
        getWidth: 9, widthMinPixels: 6, capRounded: true, pickable: true, onClick: pick,
        updateTriggers: { getColor: [pickedId] },
      }));
      out.push(new ScatterplotLayer({
        id: 'pb-candidate-junctions', data: junctions, getPosition: (d) => d.position,
        radiusUnits: 'pixels', getRadius: 9, getFillColor: [56, 189, 248, 230],
        stroked: true, getLineColor: [255, 255, 255, 230], lineWidthMinPixels: 2,
        pickable: true, onClick: pick,
      }));
    }
    if (chosen && pbTargets?.features?.length) {
      const sz = pbTargets.kind === 'school_zone';
      out.push(new PathLayer({
        id: 'pb-targets', data: pbTargets.features, getPath: (f) => f.geometry.coordinates,
        getColor: (f) => (f.properties.closed ? [236, 72, 153, 230]
                          : sz ? [245, 158, 11, 110] : [56, 189, 248, 150]),
        getWidth: sz ? 2 : 12, widthMinPixels: sz ? 1 : 7, capRounded: true,
      }));
    }
    if (chosen && pbResult?.features?.length) {
      out.push(new PathLayer({
        id: 'pb-delta', data: pbResult.features, getPath: (f) => f.geometry.coordinates,
        getColor: (f) => vcDeltaColor(f.properties.deltaVc, 220),
        getWidth: 4, widthMinPixels: 2, pickable: true,
        onHover: ({ object, x, y }) => {
          const tooltip = document.getElementById('tooltip');
          if (!object) { tooltip.style.display = 'none'; return; }
          const pr = object.properties;
          const canvas = document.querySelector('canvas');
          const rect = canvas ? canvas.getBoundingClientRect() : { left: 0, top: 0 };
          tooltip.style.display = 'block';
          tooltip.style.left = `${rect.left + x + 10}px`;
          tooltip.style.top = `${rect.top + y + 10}px`;
          tooltip.innerHTML = `<b>${pr.name || pr.edgeId}</b><br/>
            <b>V/C:</b> ${Number(pr.vcA).toFixed(3)} &rarr; ${Number(pr.vcB).toFixed(3)}<br/>
            <b>Vehicles:</b> ${Math.round(pr.vehA).toLocaleString()} &rarr; ${Math.round(pr.vehB).toLocaleString()}`;
        },
      }));
    }
    if (chosen && pbBands?.outlines?.length) {
      out.push(new PathLayer({
        id: 'pb-bands-outline', data: pbBands.outlines, getPath: (o) => o.ring,
        getColor: [...BAND_OUTLINE, 230], getWidth: 5, widthMinPixels: 3,
      }));
    }
    if (chosen && pbTargets?.junction) {
      out.push(new ScatterplotLayer({
        id: 'pb-junction', data: [pbTargets.junction], getPosition: (j) => [j.lng, j.lat],
        radiusUnits: 'pixels', getRadius: 11, filled: false, stroked: true,
        getLineColor: [255, 255, 255, 240], lineWidthMinPixels: 3,
      }));
    }
    setLayers((prev) => [...prev.filter((l) => l && !PB_LAYER_IDS.includes(l.id)), ...out]);
  }, [showCongestionPricing, showSchoolZones, showNetworkEditor, prebakedOnly, prebaked,
      pbCandidates, pbChoice, pbTargets, pbResult, pbBands]);

  // Update school zone layers
  useEffect(() => {
    if (!showSchoolZones || !szData) {
      setLayers(prev => prev.filter(l => l && !SZ_LAYER_IDS.includes(l.id)));
      return;
    }
    const szLayers = getSchoolZoneLayers();
    setLayers(prev => [
      ...prev.filter(l => l && !SZ_LAYER_IDS.includes(l.id)),
      ...szLayers
    ]);
  }, [showSchoolZones, szData, szShowAffected, szShowHeatmap, szShowNames, caseStudyRegion]);

  const CP_LAYER_IDS = ['cp-crz-fill', 'cp-crz-border', 'cp-tolled-roads', 'cp-vc-heatmap',
                        'cp-reroute-plain', 'cp-reroute-cp'];

  // Build congestion pricing layers
  const getCongestionPricingLayers = () => {
    if (!cpData || !showCongestionPricing || !caseStudyRegion) return [];
    const cpLayers = [];

    // CRZ boundary — fill only (no stroke). Multi-part: the gaps between parts
    // are the FDR / West Side Highway corridors the CBD legislation exempts.
    const crzPolygons = cpData.crzBoundary.map(ring => ({ polygon: ring }));
    cpLayers.push(new PolygonLayer({
      id: 'cp-crz-fill',
      data: crzPolygons,
      getPolygon: d => d.polygon,
      getFillColor: [245, 158, 11, 45],
      filled: true,
      stroked: false,
    }));

    // CRZ boundary — solid orange outline
    cpLayers.push(new PathLayer({
      id: 'cp-crz-border',
      data: cpData.crzBoundary.map(ring => ({ path: ring })),
      getPath: d => d.path,
      getColor: [245, 158, 11, 220],
      getWidth: 4,
      widthMinPixels: 2,
    }));

    // Toggle A: Tolled roads (orange)
    if (cpShowTolled) {
      cpLayers.push(new PathLayer({
        id: 'cp-tolled-roads',
        data: cpData.tolledEdges,
        getPath: d => d.path,
        getColor: [230, 126, 34, 200],
        getWidth: 3,
        widthMinPixels: 2,
      }));
    }

    // Toggle B: V/C ratio change heatmap
    if (cpShowHeatmap) {
      cpLayers.push(new PathLayer({
        id: 'cp-vc-heatmap',
        data: cpData.vcChange,
        getPath: d => d.path,
        getColor: d => getVcChangeColor(d.delta_vc),
        getWidth: 3,
        widthMinPixels: 2,
        pickable: true,
        onHover: ({ object, x, y }) => {
          const tooltip = document.getElementById('tooltip');
          if (object) {
            const canvas = document.querySelector('canvas');
            const rect = canvas ? canvas.getBoundingClientRect() : { left: 0, top: 0 };
            tooltip.style.display = 'block';
            tooltip.style.left = `${rect.left + x + 10}px`;
            tooltip.style.top = `${rect.top + y + 10}px`;
            tooltip.innerHTML = `<b>V/C Change:</b> ${object.delta_vc > 0 ? '+' : ''}${object.delta_vc.toFixed(4)}<br/><b>Plain V/C:</b> ${object.plain_vc.toFixed(4)}<br/><b>CP V/C:</b> ${object.cp_vc.toFixed(4)}`;
          } else {
            tooltip.style.display = 'none';
          }
        }
      }));
    }

    // Toggle C: sampled before/after route pairs. Red = route taken without the
    // zone, blue = route taken with it — the same vehicle, so the pair shows the
    // diversion itself rather than the net flow change.
    if (cpShowReroute && cpData.reroutedTrips) {
      const trips = cpData.reroutedTrips.trips;
      cpLayers.push(new PathLayer({
        id: 'cp-reroute-plain',
        data: trips,
        getPath: d => d.plain_path,
        getColor: [231, 76, 60, 130],
        getWidth: 2,
        widthMinPixels: 1,
      }));
      cpLayers.push(new PathLayer({
        id: 'cp-reroute-cp',
        data: trips,
        getPath: d => d.cp_path,
        getColor: [52, 152, 219, 150],
        getWidth: 2,
        widthMinPixels: 1,
        pickable: true,
        onHover: ({ object, x, y }) => {
          const tooltip = document.getElementById('tooltip');
          if (object) {
            const canvas = document.querySelector('canvas');
            const rect = canvas ? canvas.getBoundingClientRect() : { left: 0, top: 0 };
            tooltip.style.display = 'block';
            tooltip.style.left = `${rect.left + x + 10}px`;
            tooltip.style.top = `${rect.top + y + 10}px`;
            tooltip.innerHTML = `<b>Trip ${object.id}</b><br/><b>CRZ edges:</b> ${object.plain_crz_edges} &rarr; ${object.cp_crz_edges}`;
          } else {
            tooltip.style.display = 'none';
          }
        }
      }));
    }

    return cpLayers;
  };

  // Load congestion pricing data
  useEffect(() => {
    if (!showCongestionPricing || !caseStudyRegion) {
      setLayers(prev => prev.filter(l => l && !CP_LAYER_IDS.includes(l.id)));
      return;
    }
    if (cpData) return;

    setCpLoading(true);
    Promise.all([
      fetch('/data/congestion-pricing/crz_boundary.json').then(r => r.json()),
      fetch('/data/congestion-pricing/cp_tolled_edges.json').then(r => r.json()),
      fetch('/data/congestion-pricing/cp_vc_change.json').then(r => r.json()),
      fetch('/data/congestion-pricing/cp_rerouted_trips.json').then(r => r.json()),
    ]).then(([crzBoundary, tolledEdges, vcChange, reroutedTrips]) => {
      setCpData({ crzBoundary, tolledEdges, vcChange, reroutedTrips });
      setCpLoading(false);
    }).catch(err => {
      console.error('Failed to load congestion pricing data:', err);
      setCpLoading(false);
    });
  }, [showCongestionPricing, caseStudyRegion]);

  // Update congestion pricing layers
  useEffect(() => {
    if (!showCongestionPricing || !cpData || !caseStudyRegion) {
      setLayers(prev => prev.filter(l => l && !CP_LAYER_IDS.includes(l.id)));
      return;
    }
    const cpLayers = getCongestionPricingLayers();
    setLayers(prev => [
      ...prev.filter(l => l && !CP_LAYER_IDS.includes(l.id)),
      ...cpLayers
    ]);
  }, [showCongestionPricing, cpData, cpShowTolled, cpShowHeatmap, cpShowReroute, caseStudyRegion]);

  // ---- Network Editor: another metro invalidates everything held ----
  useEffect(() => {
    if (!neLoadedCbsa || neLoadedCbsa === neCbsa) return;
    resetNe();
    setNeLoadedCbsa(null);
    setNeEdges(null);
    setNeTls(null);
    setNeApplied(null);
    setNeManifest(null);
    setNeSummary(null);
    setNeComparison(null);
    setNeSafetyBaseline(null);
    setNeBakedDemand(null);
  }, [neCbsa, neLoadedCbsa]);

  // ---- Network Editor: load the selected metro's network ----
  useEffect(() => {
    if (!showNetworkEditor || !(neWarm || neServiceFailed)
        || (neLoadedCbsa === neCbsa && neEdges)) return undefined;
    let cancelled = false;
    setNeLoading(true);
    // The fixture describes one metro: its baked scenarios and safety baseline
    // are another region's numbers unless the selected metro IS that one.
    Promise.all([
      fetchManifest().catch(() => null),
      fetchEdges(neCbsa),
      fetchTls(neCbsa),
      fetchSafetyBaseline(),
    ])
      .then(([manifest, edges, tls, safety]) => {
        if (cancelled) return;
        const own = manifest && String(manifest.cbsa_id) === neCbsa ? manifest : null;
        setNeManifest(own);
        setNeEdges(edges);
        setNeTls(tls);
        setNeSafetyBaseline(own ? safety : null);
        setNeScenario(own?.scenarios?.[0]?.id || 'baseline');
        setNeLoadedCbsa(neCbsa);
        // No baked scenario to apply: the network draws unpainted.
        if (!own) setNeApplied(applyScenario(edges, null, null));
        const c = activeDataset?.center || centerOfJunctions(tls);
        if (c && flownDatasetRef.current !== flyKey(activeDataset)) {
          flownDatasetRef.current = flyKey(activeDataset);
          setViewState(vs => ({ ...vs, ...c, zoom: c.zoom ?? 11.5 }));
        }
        setNeLoading(false);
      })
      .catch(err => {
        if (cancelled) return;
        console.error('Failed to load network editor data:', err);
        setNeLoading(false);
      });
    return () => { cancelled = true; };
  }, [showNetworkEditor, neWarm, neServiceFailed, neCbsa]);

  // A metro with no produced run has no centre from the serving layer; the
  // signalized junctions give one without downloading the whole network.
  useEffect(() => {
    if (!neWarm || activeDataset?.center || flownDatasetRef.current === flyKey(activeDataset)) {
      return undefined;
    }
    let cancelled = false;
    flownDatasetRef.current = flyKey(activeDataset);
    fetchTls(neCbsa)
      .then((tls) => {
        const c = centerOfJunctions(tls);
        if (!cancelled && c) setViewState(vs => ({ ...vs, ...c }));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [neWarm, neCbsa, activeDataset]);

  // ---- Network Editor: (re)apply a scenario when it changes ----
  // The signal scenario has no per-edge metrics (it is a per-approach microsim), so it
  // keeps baseline colouring and only loads its comparison.
  useEffect(() => {
    if (!showNetworkEditor || !neEdges || !neScenario || !neManifest) return;
    const def = neManifest.scenarios.find(s => s.id === neScenario);
    if (!def) return;
    let cancelled = false;

    const metricsSource = def.metrics ? neScenario : 'baseline';
    Promise.all([
      fetchMetrics(metricsSource),
      def.edited_edges_file ? fetchEdits(neScenario).catch(() => null) : Promise.resolve(null),
      def.comparison ? fetchComparison(neScenario).catch(() => null) : Promise.resolve(null),
    ]).then(([metrics, edits, comparison]) => {
      if (cancelled) return;
      setNeApplied(applyScenario(neEdges, metrics, edits));
      setNeSummary(def.metrics ? metrics.summary : null);
      setNeComparison(comparison);
      const scale = neManifest.demand_scale || 1;
      setNeBakedTrips(metrics.vehicles != null
        ? Math.round(metrics.vehicles / scale) : null);
      // Signal scenario: zoom to the retimed junction so its effect is visible.
      if (def.analysis === 'microsim' && comparison) {
        setViewState(vs => ({ ...vs, longitude: comparison.lon, latitude: comparison.lat, zoom: 15 }));
      }
    }).catch(err => console.error('Failed to load scenario', neScenario, err));
    return () => { cancelled = true; };
  }, [showNetworkEditor, neEdges, neScenario, neManifest]);

  // Gated on the toggle, not loaded with the network: ~400 kB most sessions
  // never look at, and a service that is down must not delay the editor.
  useEffect(() => {
    if (!showNetworkEditor || !neShowFlows || neBakedDemand) return undefined;
    let cancelled = false;
    setNeFlowLoading(true);
    setNeFlowError(null);
    fetchBakedDemand(neCbsa)
      .then((d) => {
        if (cancelled) return;
        setNeBakedDemand(d);
        setNeFlowLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to load the baked demand:', err);
        setNeFlowError('Trip flows could not be loaded — the simulation service '
          + 'is not reachable.');
        setNeFlowLoading(false);
      });
    return () => { cancelled = true; };
  }, [showNetworkEditor, neShowFlows, neBakedDemand]);

  // Same precedence as the panel's summary line: the layer's total has to equal
  // the "Will simulate N trips" figure printed above it.
  const neFlowScale = neDemand?.scale ?? neManifest?.demand_scale ?? 1;

  // MUST stay referentially stable. The layer effect re-runs on things as
  // frequent as a road hover, and flowmap.gl re-clusters 7,684 flows whenever
  // the identity of `data` or `filter` changes.
  const neFlows = useMemo(
    () => (neShowFlows ? buildTripFlowData(neBakedDemand, neDemand, neFlowScale) : null),
    [neShowFlows, neBakedDemand, neDemand, neFlowScale]);

  const neFlowFilter = useMemo(
    () => (!neFlowZone ? undefined : {
      selectedLocations: [neFlowZone.id],
      // REQUIRED with selectedLocations: flowmap.gl has no default, and without
      // it isFlowInSelection returns undefined for every flow — a blank map.
      locationFilterMode: LocationFilterMode.ALL,
    }),
    [neFlowZone]);

  // On screen, not just toggled on: the matrix may still be loading or missing.
  const neFlowsLive = !!(neShowFlows && neFlows);
  const neSignalsAutoHidden = neFlowsLive;
  const neSignalsVisible = neShowSignals && !neSignalsAutoHidden;

  // Zones without lines. Done by dropping flowmap.gl's line sublayer, NOT by
  // filtering flows out: location-circle sizes are computed from the flows that
  // pass the filter, so an empty selection would shrink every circle to nothing.
  const neFlowLinesHidden = neFlowMode === 'zone' && !neFlowZone;
  const neFlowSubLayerProps = useMemo(
    // flowmap.gl's own sublayer id for flowLinesRenderingMode 'curved'.
    () => (neFlowLinesHidden ? { 'curved-flow-lines': { visible: false } } : undefined),
    [neFlowLinesHidden]);

  useEffect(() => { setNeFlowZone(null); },
    [neBakedDemand, neDemand, neShowFlows, neFlowMode]);

  const neFlowSummary = {
    loading: neFlowLoading,
    error: neFlowError,
    trips: neFlows?.trips,
    pairs: neFlows?.pairs,
    zoneName: neFlowZone?.name || null,
  };

  // ---- Network Editor: edits are staged, then simulated ----
  const neRemovedSet = new Set(neEdits.removed);
  const neHasEdits = neEdits.removed.length > 0 || neEdits.added.length > 0
    || Object.keys(neEdits.attrs).length > 0 || !!neEdits.signal;

  // A physical road is several SUMO edges: one per direction (reverse carriageway carries
  // a leading "-"), each split at junctions.
  const roadScope = (props, bothDirections) => {
    if (!neApplied) return [{ properties: props }];
    const rev = props.id.startsWith('-');
    const feats = neApplied.features.filter(f =>
      f.properties.way_id === props.way_id &&
      (bothDirections || f.properties.id.startsWith('-') === rev));
    return feats.length ? feats : [{ properties: props }];
  };
  const scopeOf = (props, both) => {
    const feats = roadScope(props, both);
    return {
      ids: feats.map(f => f.properties.id),
      lengthM: Math.round(feats.reduce((s, f) => s + (f.properties.length_m || 0), 0)),
    };
  };

  // Rough compass bearing, so "this direction" means something on screen.
  const bearingOf = (id) => {
    const f = neApplied?.features.find(x => x.properties.id === id);
    if (!f) return '';
    const c = f.geometry.coordinates;
    const [x1, y1] = c[0];
    const [x2, y2] = c[c.length - 1];
    const deg = (Math.atan2(x2 - x1, y2 - y1) * 180 / Math.PI + 360) % 360;
    return ['north', 'north-east', 'east', 'south-east',
            'south', 'south-west', 'west', 'north-west'][Math.round(deg / 45) % 8] + 'bound';
  };

  const selectNeRoad = (props) => {
    // Clicking the selected road again deselects it.
    if (neSelected?.kind === 'road' && neSelected.props.id === props.id) {
      setNeSelected(null);
      return;
    }
    // Pick the source first, then look up in it. Falling through per edge would answer with
    // traffic from a run the map is not drawing.
    const src = neRunResult?.edges || neDemandBaseline?.edges;
    const live = src ? src[props.id] : props.metric;
    setNeSelected({
      kind: 'road',
      oneWay: scopeOf(props, false),
      bothWays: scopeOf(props, true),
      bearing: bearingOf(props.id),
      props: {
        ...props,
        ...(neEdits.attrs[props.id] || {}),
        // No `|| props.metric` fallback: `live` already resolved the precedence, and a miss
        // under the winning source is a miss.
        metric: live ?? null,
        removed: props.removed || neRemovedSet.has(props.id),
      },
    });
  };
  const nameOfEdge = (eid) =>
    neApplied?.features.find(f => f.properties.id === eid)?.properties.name || '';

  const selectNeJunction = (d) => {
    if (neSelected?.kind === 'junction' && neSelected.junctionId === d.junction_id) {
      setNeSelected(null);
      return;
    }
    const tlsId = d.tls_ids?.[0] || d.junction_id;
    const prog = d.programs?.[tlsId];
    setNeSelected({ kind: 'junction', junctionId: d.junction_id, tlsId,
      label: [...new Set(d.incoming.map(e => e.name).filter(Boolean))].join(' / '),
      connections: (d.connections || []).map(c => ({
        ...c,
        fromName: nameOfEdge(c.from),
      })),
      program: prog });
    setViewState(vs => ({ ...vs, longitude: d.lon, latitude: d.lat, zoom: Math.max(vs.zoom, 15) }));
  };
  const clearNeSelection = () => setNeSelected(null);

  const closeNeRoad = (props, group) => {
    const ids = group && group.length ? group : [props.id];
    setNeEdits(e => ({ ...e, removed: [...new Set([...e.removed, ...ids])] }));
    setNeSelected(null);
  };
  const reopenNeRoad = (props, group) => {
    const ids = new Set(group && group.length ? group : [props.id]);
    setNeEdits(e => ({ ...e, removed: e.removed.filter(id => !ids.has(id)) }));
    setNeSelected(null);
  };
  const changeNeAttrs = (id, attrs) => {
    setNeEdits(e => ({ ...e, attrs: { ...e.attrs, [id]: { ...(e.attrs[id] || {}), ...attrs } } }));
  };
  const stageNeSignal = (junctionId, tlsId, phases) => {
    setNeEdits(e => ({ ...e, signal: { junctionId, tlsId, phases } }));
    setNeSelected(null);
  };
  // An authored ring-barrier program. Same slot as a retiming — only one signal edit at a time.
  const stageNeProgram = (junctionId, tlsId, program) => {
    setNeEdits(e => ({ ...e, signal: program ? { junctionId, tlsId, program } : null }));
    setNeSelected(null);
  };

  const trackSafetyJob = (result, setResult, owner) => {
    if (neJobStop.current) neJobStop.current();
    if (!result?.job_id) { setNeSafetyJob(null); setNeJobOwner(null); return; }
    setNeJobOwner(owner);
    setNeSafetyJob({ status: 'queued' });
    const { promise, stop } = pollJob(result.job_id, {
      onUpdate: (j) => setNeSafetyJob(j),
    });
    neJobStop.current = stop;
    promise.then((j) => {
      if (j.status === 'cancelled') return;
      setNeSafetyJob(j);
      if (j.status === 'done') {
        setResult((prev) => (prev && prev.job_id === result.job_id
          ? { ...prev, safety: j.safety, signal: j.signal || prev.signal,
              safety_error: j.safety_error, signal_error: j.signal_error,
              // Written alongside `cost` (which stays the macro block), so applying twice is safe.
              // `cost_total` is absent when the job failed or was skipped.
              cost_total: combineCost(prev.cost, j.cost) }
          : prev));
      }
    });
  };

  // Clears the previous run's commentary, not its RESULTS: the map is drawn from them,
  // and blanking the roads for the 30 s a run takes would leave nothing on screen.
  const clearRunFeedback = () => {
    if (neJobStop.current) neJobStop.current();
    setNeSafetyJob(null);
    setNeJobOwner(null);
    setNeRunError(null);
    setNeDemandError(null);
  };

  // The custom demand rides along unchanged: an edit must be simulated under the same
  // demand its baseline was.
  const executeNeEdits = async () => {
    clearRunFeedback();
    setNeRunning(true);
    setNeRunError(null);
    try {
      const result = await runSimulation({
        ...(neCbsa ? { cbsa: neCbsa } : {}),
        ...(neDemand ? { ...neEdits, demand: neDemand } : neEdits),
      });
      setNeRunResult(result);
      setNeEditKind('simulated');
      trackSafetyJob(result, setNeRunResult, 'edit');
    } catch (err) {
      console.error('Simulation failed:', err);
      setNeRunError(err.message || 'Simulation failed');
    } finally {
      setNeRunning(false);
    }
  };

  // Runs with NO edits and keeps the result as the new baseline. Any edit result on
  // screen was computed against the old demand, so it is dropped.
  const applyNeDemand = async (demand, report) => {
    clearRunFeedback();
    setNeDemandRunning(true);
    setNeDemandError(null);
    try {
      const result = await runSimulation({ ...(neCbsa ? { cbsa: neCbsa } : {}), demand });
      setNeDemand(demand);
      setNeDemandReport(report);
      setNeDemandBaseline(result);
      setNeRunResult(null);
      setNeEditKind(null);
      trackSafetyJob(result, setNeDemandBaseline, 'demand');
    } catch (err) {
      console.error('Demand baseline run failed:', err);
      setNeDemandError(err.message || 'Could not run this demand');
    } finally {
      setNeDemandRunning(false);
    }
  };

  // Only stops the job if it belongs to the half being reset.
  const stopJobIfOwnedBy = (owner) => {
    if (neJobOwner !== owner) return;
    if (neJobStop.current) neJobStop.current();
    setNeSafetyJob(null);
    setNeJobOwner(null);
  };

  const resetNeEdits = () => {
    setNeScenario('baseline');
    setNeEditKind(null);
    setNeEdits({ removed: [], added: [], attrs: {} });
    setNeRunResult(null);
    setNeRunError(null);
    setNeSelected(null);
    stopJobIfOwnedBy('edit');
  };

  const resetNeBaseline = () => {
    setNeDemand(null);
    setNeDemandReport(null);
    setNeDemandBaseline(null);
    setNeDemandError(null);
    setNeDemandResetKey((k) => k + 1);
    // The edit run was measured against the demand being thrown away, so it goes. The
    // staged edits survive: they mean the same thing against the baked demand.
    setNeRunResult(null);
    setNeEditKind(null);
    if (neJobStop.current) neJobStop.current();
    setNeSafetyJob(null);
    setNeJobOwner(null);
  };

  // neBakedDemand is kept: it is the bake and cannot have changed.
  const resetNe = () => {
    resetNeEdits();
    resetNeBaseline();
    setNeShowFlows(false);
    setNeFlowZone(null);
    setNeFlowMode('all');
  };

  // Assemble a printable report. `result` defaults to the edit run; a demand-only change
  // passes its own baseline result.
  const buildNeReport = (result = neRunResult) => {
    if (!result || !neSummary) return null;
    const isDemandRun = result === neDemandBaseline;
    // "Before" is whatever this run was measured against: the demand baseline when one is applied, else the bake.
    const baseMetrics = {};
    if (!isDemandRun && neDemandBaseline?.edges) {
      Object.assign(baseMetrics, neDemandBaseline.edges);
    } else {
      for (const f of neApplied?.features || []) {
        if (f.properties.metric) baseMetrics[f.properties.id] = f.properties.metric;
      }
    }

    // Biggest movers either way — where the edit actually landed.
    const moved = [];
    for (const [id, after] of Object.entries(result.edges)) {
      const bef = baseMetrics[id];
      if (!bef) continue;
      const d = after.vc - bef.vc;
      if (Math.abs(d) > 0.05) {
        moved.push({ id, name: nameOfEdge(id), vcBefore: bef.vc,
                     vcAfter: after.vc, losAfter: after.los, d });
      }
    }
    moved.sort((a, b) => b.d - a.d);

    const editLines = [];
    if (neDemand) {
      // `fleet` absent = the network's stock mix.
      const fleetNote = neDemand.fleet?.shares
        ? Object.entries(neDemand.fleet.shares)
          .map(([id, pct]) => `${id} ${pct}%`)
          .join(' / ')
        : 'stock vehicle mix';
      const tazReport = neDemandReport?.report?.taz;
      editLines.push({
        label: 'Custom demand',
        detail: [
          neDemand.od?.length
            ? `${neDemand.od.length.toLocaleString()} OD pairs (${neDemand.od_format}) ×${neDemand.scale ?? 1}`
            : `baked OD matrix ×${neDemand.scale ?? 1}`,
          neDemand.taz_xml
            ? `custom zones${tazReport ? ` (${tazReport.zones_in_network} of ${tazReport.zones} in network)` : ''}`
            : 'baked zones',
          fleetNote,
          neDemandReport?.report?.effective_trips != null
            ? `${Math.round(neDemandReport.report.effective_trips).toLocaleString()} trips simulated`
            : '',
        ].filter(Boolean).join(' · '),
      });
    }
    const editableRun = !isDemandRun;
    const closedByName = {};
    if (editableRun) neEdits.removed.forEach((id) => {
      const n = nameOfEdge(id) || '(unnamed road)';
      closedByName[n] = (closedByName[n] || 0) + 1;
    });
    Object.entries(closedByName).forEach(([n, c]) => editLines.push({
      label: 'Closed road', detail: `${n}${c > 1 ? ` (${c} segments)` : ''}`,
    }));
    Object.entries(neEdits.attrs).forEach(([id, a]) => {
      // Staged attrs are km/h on the wire; the report prints mph.
      const parts = [];
      if (a.lanes != null) parts.push(`${a.lanes} lanes`);
      if (a.speed_kmh != null) parts.push(`@ ${kmhToMph(a.speed_kmh)} mph`);
      editLines.push({
        label: 'Changed road',
        detail: `${nameOfEdge(id) || id} — ${parts.join(' ')}`,
      });
    });
    if (editableRun && neEdits.signal) {
      const sig = neEdits.signal;
      const where = result.signal?.junction_label || sig.junctionId;
      editLines.push(sig.program ? {
        label: 'Ring-barrier program',
        detail: `${where} — ${sig.program.phases.length} phases, `
          + `${sig.program.cycle}s cycle, offset ${sig.program.offset}s `
          + `(${sig.program.controllerType})`,
      } : {
        label: 'Retimed signal',
        detail: `${where} — ${sig.phases.length} phases, `
          + `${Math.round(sig.phases.reduce((s, p) => s + p.duration, 0))}s cycle`,
      });
    }

    return {
      region: neManifest?.region || metroName || `CBSA ${neCbsa}`,
      hour: neManifest?.hour ?? 8,
      weekday: neManifest?.weekday ?? 1,
      // The multiplier this run used — the custom demand's own once one is applied. One
      // number covers both engines; final fallback is the bake's own, not a literal.
      demandScale: neDemand?.scale ?? neManifest?.demand_scale ?? 1,
      // The multiplier the "before" column ran at, which is not always the run's own: a
      // custom-demand run's "before" is the baked x1. Same predicate as `before` and
      // `safetyBefore` below — the three must pick the same side.
      beforeScale: (!isDemandRun && neDemandBaseline)
        ? (neDemand?.scale ?? 1)
        : (neManifest?.demand_scale ?? 1),
      generatedAt: new Date().toISOString(),
      edits: editLines,
      before: (!isDemandRun && neDemandBaseline?.summary) || neSummary,
      after: result.summary,
      changed: { worse: moved.slice(0, 10), better: moved.slice(-10).reverse() },
      signal: result.signal || null,
      // `safetyBefore` follows the same rule as `before`: the demand baseline's own safety
      // when a custom demand is applied, otherwise the baked one.
      safety: result.safety
        ? { ...result.safety,
            top_edges: (result.safety.top_edges || []).map((e) => ({
              ...e, name: nameOfEdge(e.edge) || e.edge,
            })) }
        : null,
      safetyBefore: (!isDemandRun && neDemandBaseline?.safety) || neSafetyBaseline || null,
      safetyError: result.safety_error || null,
      // Set when the feasibility gate stopped the microsim (distinct from safetyError).
      safetyBlocked: result.safety_blocked || null,
      safetyPending: !!result.microsim_pending && !result.safety,
      signalError: result.signal_error || null,
      runSeconds: result.elapsed_s,
      vehicles: result.vehicles,
    };
  };

  // Hand the payload to the report tab through localStorage -- the app's
  // existing convention for a full-page result.
  const openNeReport = (result) => {
    const payload = buildNeReport(result);
    if (!payload) return;
    localStorage.setItem('networkEditReport', JSON.stringify(payload));
    window.open(`${window.location.origin}/home?view=edit-report`, '_blank');
  };

  const downloadNeReport = (result) => {
    const payload = buildNeReport(result);
    if (!payload) return;
    const stamp = payload.generatedAt.replace(/[:.]/g, '-').slice(0, 19);
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${payload.region.toLowerCase().replace(/\W+/g, '-')}-demand-report-${stamp}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const NE_LAYER_IDS = ['ne-flow-scrim', 'ne-edges', 'ne-added', 'ne-tls', 'ne-tls-icon', 'ne-highlight', 'ne-closed', 'ne-closed-marks', 'ne-flows'];

  // A boolean, not viewState.zoom: this feeds the layer effect's dependency
  // list, and a raw zoom would rebuild a 15,340-feature GeoJsonLayer on every
  // frame of a pinch. This flips exactly twice -- once in, once out.
  const tlsAsIcons = (viewState?.zoom ?? 11) >= NE_TLS_ICON_ZOOM;

  const neFlowInt = (n) => Math.round(Number(n) || 0).toLocaleString();
  const neFlowEndName = (l) => l?.name || (l?.id != null ? `Zone ${l.id}` : '—');

  const onNeFlowHover = (info) => {
    const tooltip = document.getElementById('tooltip');
    if (!tooltip) return;
    const obj = info?.object;
    if (!obj) { tooltip.style.display = 'none'; return; }
    const canvas = document.querySelector('canvas');
    const rect = canvas ? canvas.getBoundingClientRect() : { left: 0, top: 0 };
    tooltip.style.display = 'block';
    tooltip.style.left = `${rect.left + info.x + 10}px`;
    tooltip.style.top = `${rect.top + info.y + 10}px`;
    if (obj.type === PickingType.FLOW) {
      tooltip.innerHTML = `<b>${neFlowEndName(obj.origin)} → ${neFlowEndName(obj.dest)}</b>`
        + `<br/>${neFlowInt(obj.count)} trips`;
    } else {
      const t = obj.totals || {};
      tooltip.innerHTML = `<b>${obj.name || `Zone ${obj.id}`}</b>`
        + `<br/>Out ${neFlowInt(t.outgoingCount)} · In ${neFlowInt(t.incomingCount)}`
        + `<br/>Within ${neFlowInt(t.internalCount)} trips`;
    }
  };

  // A view filter only; nothing here edits the matrix.
  const onNeFlowClick = (info) => {
    const obj = info?.object;
    if (!obj || obj.type !== PickingType.LOCATION) return;
    setNeFlowZone((cur) => (cur && String(cur.id) === String(obj.id)
      ? null
      : { id: obj.id, name: obj.name || `Zone ${obj.id}` }));
  };

  const getNetworkEditorLayers = () => {
    if (!showNetworkEditor || !neApplied) return [];
    const layersOut = [];

    // Base network coloured by V/C (LOS); closed edges render faint. Three sources, most
    // recent wins WHOLESALE — the winner is picked before the lookup, not per edge.
    const liveMetrics = neRunResult?.edges || neDemandBaseline?.edges;
    const metricOf = (f) => (liveMetrics ? liveMetrics[f.properties.id] : f.properties.metric);
    // A metro with no produced run has nothing to colour by, so the network is
    // drawn as a network. Not the "no modelled traffic" grey: there, a faint
    // edge means something, and here every edge would be faint.
    const unpainted = !liveMetrics && !neManifest;
    const isClosed = (f) => f.properties.removed || neRemovedSet.has(f.properties.id);

    // Identity while the flows are off, which keeps the layer-off map pixel-identical.
    const dim = neFlowsLive
      ? (c) => [c[0], c[1], c[2], Math.round((c[3] ?? 255) * 0.3)]
      : (c) => c;

    // Carriageways draw on true centrelines. A lateral offset breaks hover: the highlight
    // and closure layers draw un-offset.
    layersOut.push(new GeoJsonLayer({
      id: 'ne-edges',
      data: { type: 'FeatureCollection', features: neApplied.features },
      stroked: true,
      lineWidthUnits: 'pixels',
      getLineColor: f => dim(isClosed(f)
        ? [90, 90, 90, 70]
        : unpainted ? NE_UNPAINTED_COLOR
        : losColor(metricOf(f) ? metricOf(f).vc : null)),
      getLineWidth: f => (isClosed(f) ? 1
        : unpainted ? 3 : widthForCount(metricOf(f)?.count)),
      lineWidthMinPixels: 1,
      pickable: true,
      onHover: ({ object }) => setNeHoverId(object ? object.properties.id : null),
      onClick: ({ object }) => object && selectNeRoad(object.properties),
      updateTriggers: {
        getLineColor: [neScenario, neEdits, neRunResult, neDemandBaseline, neFlowsLive,
                       neManifest],
        getLineWidth: [neScenario, neEdits, neRunResult, neDemandBaseline, neManifest],
      },
    }));

    const closedFeats = neApplied.features.filter(isClosed);
    if (closedFeats.length) {
      layersOut.push(new GeoJsonLayer({
        id: 'ne-closed',
        data: { type: 'FeatureCollection', features: closedFeats },
        stroked: true,
        lineWidthUnits: 'pixels',
        getLineColor: [70, 70, 78, 255],
        getLineWidth: 5,
        lineWidthMinPixels: 3,
        pickable: false,
        parameters: { depthTest: false },
      }));
      // Barriers sit at the ends of a closed stretch, so mark the start and end
      // of each closed edge (deduped where segments meet).
      // Plain object, not a JS Map: `Map` is the react-map-gl component here.
      const ends = {};
      for (const f of closedFeats) {
        const c = f.geometry.coordinates;
        for (const pt of [c[0], c[c.length - 1]]) {
          ends[`${pt[0].toFixed(5)},${pt[1].toFixed(5)}`] = pt;
        }
      }
      layersOut.push(new ScatterplotLayer({
        id: 'ne-closed-marks',
        data: Object.values(ends),
        getPosition: d => d,
        getRadius: 5,
        radiusUnits: 'pixels',
        radiusMinPixels: 4,
        getFillColor: [192, 57, 43, 255],
        getLineColor: [255, 255, 255, 230],
        stroked: true,
        lineWidthMinPixels: 2,
        pickable: false,
        parameters: { depthTest: false },
      }));
    }

    const hlId = neSelected?.kind === 'road' ? neSelected.props.id : neHoverId;
    if (hlId) {
      const hlFeat = neApplied.features.filter(f => f.properties.id === hlId);
      if (hlFeat.length) {
        layersOut.push(new GeoJsonLayer({
          id: 'ne-highlight',
          data: { type: 'FeatureCollection', features: hlFeat },
          stroked: true,
          lineWidthUnits: 'pixels',
          getLineColor: [124, 198, 68, 255],
          getLineWidth: 6,
          lineWidthMinPixels: 4,
          pickable: false,
          parameters: { depthTest: false },
        }));
      }
    }

    // Added edges (new roads that don't exist in edges.geojson) — accent color.
    if (neApplied.added.length) {
      layersOut.push(new GeoJsonLayer({
        id: 'ne-added',
        data: { type: 'FeatureCollection', features: neApplied.added },
        stroked: true,
        lineWidthUnits: 'pixels',
        getLineColor: [124, 198, 68, 255],
        getLineWidth: 4,
        lineWidthMinPixels: 3,
        pickable: false,
      }));
    }

    // Ordered above the roads, below the signal dots. `flowLinesRenderingMode`
    // rather than `animationEnabled`, which is deprecated in 9.4 and warns.
    if (neFlowsLive) {
      // Dark scrim: the flow ramp's quiet end recedes by going dark and needs a near-black
      // ground; this basemap's land is mid-grey. Above the roads as well as the map.
      layersOut.push(new PolygonLayer({
        id: 'ne-flow-scrim',
        data: [[[-180, -85], [180, -85], [180, 85], [-180, 85]]],
        getPolygon: (d) => d,
        filled: true,
        stroked: false,
        getFillColor: [8, 8, 12, 120],
        pickable: false,
        parameters: { depthTest: false },
      }));
      layersOut.push(new FlowmapLayer({
        id: 'ne-flows',
        data: neFlows.data,
        pickable: true,
        darkMode: true,
        colorScheme: NE_FLOW_SCHEME,
        clusteringEnabled: true,
        clusteringAuto: true,
        fadeEnabled: true,
        // Off, fading only multiplies HCL lightness, so the quiet end goes to
        // black rather than away. Fading opacity too lets the ground through.
        fadeOpacityEnabled: true,
        fadeAmount: neFlowFade,
        flowLinesRenderingMode: 'curved',
        locationsEnabled: true,
        locationTotalsEnabled: true,
        highlightColor: '#7CC644',
        getLocationId: (l) => l.id,
        getLocationName: (l) => l.name,
        getLocationLat: (l) => l.lat,
        getLocationLon: (l) => l.lon,
        getFlowOriginId: (f) => f.origin,
        getFlowDestId: (f) => f.dest,
        getFlowMagnitude: (f) => f.count,
        filter: neFlowFilter,
        _subLayerProps: neFlowSubLayerProps,
        onHover: onNeFlowHover,
        onClick: onNeFlowClick,
      }));
    }

    // Signalized junctions, clickable to edit timing. Two renderings switched at
    // NE_TLS_ICON_ZOOM: measured median nearest-neighbour gap is 5 px at zoom 11.5 and
    // 56 px at zoom 15, so dots are a density hint and glyphs are a control.
    if (neTls && neSignalsVisible) {
      const activeJct = neSelected?.kind === 'junction' ? neSelected.junctionId
        : (neScenario === 'signal-retime-dryden' ? NE_SIGNAL_JUNCTION : null);

      if (tlsAsIcons) {
        const uris = getSignalIconUris();
        layersOut.push(new IconLayer({
          // Distinct id, not 'ne-tls': deck.gl matches layers by id across renders, so reusing
          // one id for two layer classes carries state over and IconLayer dies on getTexture().
          id: 'ne-tls-icon',
          data: neTls,
          getPosition: d => [d.lon, d.lat],
          getIcon: d => ({
            url: d.junction_id === activeJct ? uris.active : uris.idle,
            width: 48, height: 48,
          }),
          getSize: d => (d.junction_id === activeJct ? 34 : 26),
          sizeUnits: 'pixels',
          sizeMinPixels: 22,
          sizeMaxPixels: 40,
          pickable: true,
          onClick: ({ object }) => object && selectNeJunction(object),
          updateTriggers: { getIcon: [activeJct], getSize: [activeJct] },
        }));
      } else {
        layersOut.push(new ScatterplotLayer({
          id: 'ne-tls',
          data: neTls,
          getPosition: d => [d.lon, d.lat],
          // Radius is what deck.gl picks against; radiusMinPixels keeps the target honest when
          // zoomed out. Kept under the road stroke widths (max 5 px).
          getRadius: d => (d.junction_id === activeJct ? 11 : 7),
          radiusUnits: 'pixels',
          radiusMinPixels: 6,
          getFillColor: d => (d.junction_id === activeJct ? [255, 200, 0, 255] : [255, 255, 255, 225]),
          getLineColor: d => (d.junction_id === activeJct ? [255, 140, 0, 255] : [30, 30, 30, 255]),
          stroked: true,
          lineWidthMinPixels: 1.5,
          pickable: true,
          onClick: ({ object }) => object && selectNeJunction(object),
          updateTriggers: { getFillColor: [activeJct], getLineColor: [activeJct], getRadius: [activeJct] },
        }));
      }
    }

    return layersOut;
  };

  useEffect(() => {
    if (!showNetworkEditor) {
      setLayers(prev => prev.filter(l => l && !NE_LAYER_IDS.includes(l.id)));
      return;
    }
    const neLayers = getNetworkEditorLayers();
    setLayers(prev => [
      ...prev.filter(l => l && !NE_LAYER_IDS.includes(l.id)),
      ...neLayers,
    ]);
  }, [showNetworkEditor, neApplied, neTls, neScenario, neSelected, neHoverId, neEdits,
      neRunResult, neDemandBaseline, tlsAsIcons, neSignalsVisible,
      neFlowsLive, neFlows, neFlowFilter, neFlowSubLayerProps, neFlowFade]);

  const sizeOptions = [
    { label: 'Extra Large', value: 6 },
    { label: 'Large', value: 7 },
    { label: 'Medium', value: 8 },
    { label: 'Small', value: 9 }
  ];

  const getCurrentSizeLabel = () => {
    const option = sizeOptions.find(opt => opt.value === hexResolution);
    return option ? option.label : 'Medium';
  };

  const handleHexSizeChange = (event) => {
    const newResolution = parseInt(event.target.value);
    setHexResolution(newResolution);
    hexIdToCoordinates.current.clear();
  };

  const clearSelection = () => {
    setSelectedHexagons(new Set());
    hexIdToCoordinates.current.clear();
  };

  // Guarded by a ref, not an effect dep on viewState, so the user can pan away afterwards.
  useEffect(() => {
    if (!activeDataset?.center) return;
    if (isDemo() && activeDataset.kind === 'mock') return;
    if (flownDatasetRef.current === flyKey(activeDataset)) return;
    flownDatasetRef.current = flyKey(activeDataset);
    setSzData(null);
    setCpData(null);
    setMacroRunResult(null);
    setMacroDeltaFeatures(null);
    setViewState((vs) => ({ ...vs, ...activeDataset.center }));
    // A selection made in the previous region is meaningless here.
    setSelectedHexagons(new Set());
    hexIdToCoordinates.current.clear();
    setGridBbox(null);
  }, [activeDataset]);

  // Close the dataset panel when clicking outside it.
  useEffect(() => {
    function handleClickOutside(event) {
      if (datasetPanelRef.current && !datasetPanelRef.current.contains(event.target)) {
        setShowDatasetPanel(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close hex controls when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (hexControlsRef.current && !hexControlsRef.current.contains(event.target)) {
        setShowHexControls(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Every effect removes only the layers it draws; a shared removal list wiped the others'.
  const idsToRemove = ['search-result-polygon', 'link-layer', 'traffic-hexagon-layer', 'route-layer', 'poi-layer'];
  const TRAFFIC_LAYER_IDS = ['link-layer', 'traffic-hexagon-layer'];
  const ROUTE_LAYER_IDS = ['route-layer'];

  // Handle transport type selection
  const handleTransportSelect = (transportType) => {
    console.log('Transport clicked:', transportType.key, 'disabled:', transportType.disabled, 'startPoint:', !!startPoint, 'endPoint:', !!endPoint);
    if (transportType.disabled) return;

    if (selectedTransport === transportType.key) {
      // Deselect: clear route
      setSelectedTransport(null);
      if (onRouteSearch) onRouteSearch(null, null, null);
      return;
    }

    setSelectedTransport(transportType.key);
    // Auto-search if both points are set
    if (startPoint && endPoint && onRouteSearch) {
      console.log('Calling onRouteSearch with costing:', transportType.costing);
      onRouteSearch(startPoint.coord, endPoint.coord, transportType.costing);
    }
  };

  // Close shortest path UI and clear everything
  const handleCloseShortestPath = () => {
    setStartPoint(null);
    setEndPoint(null);
    setPendingPoint(null);
    setSelectedTransport(null);
    if (onCloseShortestPath) {
      onCloseShortestPath();
    }
  };

  // Determine current selection mode based on existing points
  const getCurrentSelectionMode = () => {
    if (!startPoint) return 'start';
    if (!endPoint) return 'end';
    return null; // Both points are set
  };

  // Handle map click for setting start/end points
  const handleMapClick = (info) => {
    // Network Editor: this deck-level handler reliably fires on every click,
    // so we route edge/junction picks through it (per-layer onClick is swallowed
    // by having a top-level onClick in this setup).
    if (showNetworkEditor) {
      if (neFlowZone && !info.layer) setNeFlowZone(null);
      return;
    }

    // Macro analysis drawing takes priority
    if (macroDrawingMode !== 'none') {
      handleMacroMapClick(info);
      return;
    }

    if (!showShortestPath) return;
    if (!info.coordinate) return;

    const mode = getCurrentSelectionMode();
    if (!mode) return; // Both points already set

    const [lng, lat] = info.coordinate;
    setPendingPoint({ coord: [lng, lat], type: mode });
  };

  // Confirm pending point as start or end
  const handleConfirmPoint = () => {
    if (!pendingPoint) return;

    const pointData = {
      coord: pendingPoint.coord,
      name: `${pendingPoint.coord[1].toFixed(5)}, ${pendingPoint.coord[0].toFixed(5)}`,
      source: 'map'
    };

    if (pendingPoint.type === 'start') {
      setStartPoint(pointData);
    } else {
      setEndPoint(pointData);
    }

    setPendingPoint(null);
  };

  // Cancel pending point selection
  const handleCancelPoint = () => {
    setPendingPoint(null);
  };

  // Clear a specific point (allows re-selection)
  const handleClearPoint = (type) => {
    if (type === 'start') {
      setStartPoint(null);
    } else {
      setEndPoint(null);
    }
    // Clear the route and transport selection when either point is removed
    setSelectedTransport(null);
    if (onRouteSearch) {
      onRouteSearch(null, null, null);
    }
  };

  // Calculate screen position for popup
  const getPopupScreenPosition = () => {
    if (!pendingPoint) return null;
    try {
      const viewport = new WebMercatorViewport({
        width: window.innerWidth,
        height: window.innerHeight,
        ...viewState
      });
      const [x, y] = viewport.project(pendingPoint.coord);
      return { x, y };
    } catch (e) {
      return null;
    }
  };

  // Draggable panel handlers
  const handleDragStart = (e) => {
    if (e.target.closest('input') || e.target.closest('button')) return; // Don't drag when clicking inputs or buttons
    setIsDragging(true);
    dragStartRef.current = {
      x: e.clientX - panelPosition.x,
      y: e.clientY - panelPosition.y
    };
  };

  const handleDragMove = (e) => {
    if (!isDragging) return;
    setPanelPosition(onMap(panelRef.current, {
      x: e.clientX - dragStartRef.current.x,
      y: e.clientY - dragStartRef.current.y,
    }, true));
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  // Add/remove drag event listeners
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleDragMove);
      window.addEventListener('mouseup', handleDragEnd);
    }
    return () => {
      window.removeEventListener('mousemove', handleDragMove);
      window.removeEventListener('mouseup', handleDragEnd);
    };
  }, [isDragging]);
  useKeepOnMap(panelRef, setPanelPosition, showShortestPath, true);

  // Debounced facility fetch on viewport change
  const facilityDebounceRef = useRef(null);

  const notifyViewportBounds = useCallback((vs) => {
    if (!onViewportBoundsChange) return;

    if (vs.zoom < FACILITY_MIN_ZOOM) {
      onViewportBoundsChange(null);
      return;
    }

    try {
      const vp = new WebMercatorViewport({
        width: window.innerWidth,
        height: window.innerHeight,
        longitude: vs.longitude,
        latitude: vs.latitude,
        zoom: vs.zoom,
      });
      const [west, north] = vp.unproject([0, 0]);
      const [east, south] = vp.unproject([window.innerWidth, window.innerHeight]);
      onViewportBoundsChange({ west, south, east, north });
    } catch (e) {
      console.error('Viewport bounds error:', e);
    }
  }, [onViewportBoundsChange]);

  const bboxOfViewState = (vs) => {
    const width = window.innerWidth;
    const height = window.innerHeight;
    const vp = new WebMercatorViewport({
      width, height,
      longitude: vs.longitude, latitude: vs.latitude, zoom: vs.zoom,
    });
    const [west, north] = vp.unproject([0, 0]);
    const [east, south] = vp.unproject([width, height]);
    return [west, south, east, north];
  };

  const debouncedGridBbox = useCallback((vs) => {
    if (gridDebounceRef.current) clearTimeout(gridDebounceRef.current);
    gridDebounceRef.current = setTimeout(() => setGridBbox(bboxOfViewState(vs)), 250);
  }, []);

  const debouncedFacilityFetch = useCallback((vs) => {
    if (facilityDebounceRef.current) {
      clearTimeout(facilityDebounceRef.current);
    }
    facilityDebounceRef.current = setTimeout(() => {
      notifyViewportBounds(vs);
    }, 500);
  }, [notifyViewportBounds]);

  // When showFacilities is toggled on, immediately fetch for current viewport
  useEffect(() => {
    if (showFacilities) {
      notifyViewportBounds(viewState);
    }
  }, [showFacilities]);

  // Create markers layer for start and end points
  const getMarkersLayer = () => {
    const markers = [];

    // Add pending point (semi-transparent)
    if (pendingPoint) {
      const isPendingStart = pendingPoint.type === 'start';
      markers.push({
        position: pendingPoint.coord,
        color: isPendingStart ? [255, 255, 255, 180] : [124, 198, 68, 180], // Semi-transparent
        radius: 12,
        isPending: true
      });
    }

    if (startPoint) {
      markers.push({
        position: startPoint.coord,
        color: [255, 255, 255, 255], // White for start
        radius: 12,
        isPending: false
      });
    }
    if (endPoint) {
      markers.push({
        position: endPoint.coord,
        color: [124, 198, 68, 255], // Green (#7CC644) for end
        radius: 12,
        isPending: false
      });
    }

    if (markers.length === 0) return null;

    return new ScatterplotLayer({
      id: 'route-markers-layer',
      data: markers,
      pickable: true,
      opacity: 1,
      stroked: true,
      filled: true,
      radiusScale: 1,
      radiusMinPixels: 8,
      radiusMaxPixels: 15,
      lineWidthMinPixels: 2,
      getPosition: d => d.position,
      getRadius: d => d.radius,
      getFillColor: d => d.color,
      getLineColor: d => d.isPending ? [100, 100, 100] : [50, 50, 50],
      getLineWidth: 2
    });
  };

  // The grid follows the viewport (gridBbox, updated on pan/zoom settle).
  useEffect(() => {
    const HEX_ID = 'hexagon-layer';
    if (!showHexagons) {
      setLayers(prev => prev.filter(l => l && l.id !== HEX_ID));
      setHexSuppressed(false);
      return;
    }
    const bbox = gridBbox || bboxOfViewState(viewState);
    const { cells, suppressed } = generateHexagons(bbox, hexResolution, selectedHexagons);
    setHexSuppressed(suppressed);
    const hexLayer = getHexLayer(cells, selectedHexagons, setSelectedHexagons, showHexagons, hexIdToCoordinates);
    setLayers(prev => [...prev.filter(l => l && l.id !== HEX_ID), ...(hexLayer ? [hexLayer] : [])]);
    setSelectedPolygons(hexIdToCoordinates.current);
    // viewState is intentionally not a dependency: the grid tracks the debounced
    // gridBbox, so dragging the map does not rebuild thousands of cells per frame.
  }, [selectedHexagons, showHexagons, hexResolution, gridBbox]); // eslint-disable-line

  useEffect(() => {
    setLayers(prev => prev.filter(layer => layer && layer.id !== 'search-result-polygon'));
    onSearch(selectedPolygons, dimension);
  }, [selectedPolygons]);
  
  // Display traffic density of either "link" or "hexagon" based on dimension
  useEffect(() => {
    const trafficLayer = trafficData && Object.keys(trafficData).length
      ? getTrafficLayer(dimension, trafficData, hexIdToCoordinates.current)
      : null;
    setLayers(prev => [
      ...prev.filter(layer => layer && !TRAFFIC_LAYER_IDS.includes(layer.id)),
      ...(trafficLayer ? [trafficLayer] : []),
    ]);
  }, [trafficData, dimension])

  // Display route links when routeData is available
  useEffect(() => {
    if (!routeData) {
      // Clear route layer when routeData is null
      setLayers(prev => prev.filter(layer => layer && layer.id !== 'route-layer'));
      return;
    }
    const routeLayer = getRouteLayer(routeData);
    setLayers(prev => [
      ...prev.filter(layer => layer && !ROUTE_LAYER_IDS.includes(layer.id)),
      ...(routeLayer ? [routeLayer] : []),
    ]);
  }, [routeData])

  // Update markers when start/end/pending points change
  useEffect(() => {
    const markersLayer = getMarkersLayer();
    setLayers(prev => {
      const filtered = prev.filter(layer => layer && layer.id !== 'route-markers-layer');
      return markersLayer ? [...filtered, markersLayer] : filtered;
    });
  }, [startPoint, endPoint, pendingPoint])

  // Update macro analysis polygon layers
  useEffect(() => {
    const macroLayerIds = ['macro-analysis-area', 'macro-crz-area', 'macro-drawing-vertices', 'macro-close-hint', 'macro-drawing-lines'];
    if (!showCongestionPricing && !showTrafficPanel) {
      setLayers(prev => prev.filter(layer => layer && !macroLayerIds.includes(layer.id)));
      return;
    }
    const macroLayers = getMacroLayers();
    setLayers(prev => [
      ...prev.filter(layer => layer && !macroLayerIds.includes(layer.id)),
      ...macroLayers
    ]);
  }, [showCongestionPricing, showTrafficPanel, tdArea, macroAnalysisArea, macroCrzArea, macroDrawingVertices, macroDrawingMode]);

  // Display POI facilities when facilityData is available
  useEffect(() => {
    if (!facilityData || facilityData.length === 0) {
      setLayers(prev => prev.filter(layer => layer && layer.id !== 'poi-layer'));
      return;
    }
    const poiLayer = getPOILayer(facilityData, onSelectFacility);
    setLayers(prev => [
      ...prev.filter(layer => layer && layer.id !== 'poi-layer'),
      poiLayer
    ]);
  }, [facilityData])

  // Transit investment view: ranked lines and res-8 access, from static files (scripts/rank_routes.py).
  const [inv, setInv] = useState(null); // { lines, meta, cells, routes, upgrades, rank }
  const [invError, setInvError] = useState(null);
  const [invRetry, setInvRetry] = useState(0);
  const invAsk = useRef(0);
  const [invMode, setInvMode] = useState('lines');
  const [invTarget, setInvTarget] = useState(10);
  const [invBoost, setInvBoost] = useState(0.5);
  const [invView, setInvView] = useState('modelled');
  useEffect(() => {
    const ask = ++invAsk.current;
    if (!showTransitInvestment || inv) return;
    setInvError(null);
    Promise.all([
      fetchStaticJson('/data/transit/investment.json'),
      fetchStaticJson('/data/transit/investment_hex8.json'),
      fetchStaticJson(TRANSIT_ROUTES_URL),
      fetchStaticJson('/data/transit/scenario5/transit_upgrades.json'),
      fetchStaticJson('/data/transit/scenario5/transit_rank.json'),
    ]).then(([doc, hexDoc, routes, upgrades, rank]) => {
      if (ask !== invAsk.current) return;
      setInv({ lines: doc.lines, meta: doc.meta, cells: buildInvCells(hexDoc), routes, upgrades, rank });
    }).catch((err) => {
      if (ask !== invAsk.current) return;
      console.error('[investment] failed to load:', err);
      setInvError(err.message);
    });
  }, [showTransitInvestment, inv, invRetry]);
  const [invMetric, setInvMetric] = useState('reach');
  // Same-budget presets, loaded the first time the explore view shows. While one is applied its
  // funded lines are the selection; moving a slider or the ranking returns to the target's.
  const [invEq, setInvEq] = useState(null); // { presets, budget, ntdYear }
  const [invEqError, setInvEqError] = useState(null);
  const [invEqRetry, setInvEqRetry] = useState(0);
  const invEqAsk = useRef(0);
  const [invPreset, setInvPreset] = useState(null);
  useEffect(() => {
    const ask = ++invEqAsk.current;
    if (!showTransitInvestment || !inv || invEq || (invView === 'modelled' && inv.upgrades)) return;
    setInvEqError(null);
    Promise.all([...SAME_BUDGET.map((key) => fetchStaticJson(sameBudgetUrl(key))), fetchStaticJson(SAME_BUDGET_COSTS_URL)])
      .then((docs) => {
        if (ask !== invEqAsk.current) return;
        const presets = sameBudgetPresets(inv.lines, inv.cells, inv.meta.total_weekday_trips,
          SAME_BUDGET.map((key, k) => ({ key, doc: docs[k] })));
        presets.forEach((p) => {
          if (p.missing.length) console.warn(`[investment] ${p.key}: funded lines not in investment.json:`, p.missing);
        });
        const costs = docs[SAME_BUDGET.length];
        setInvEq({ presets, budget: fundedCost(costs), ntdYear: costs.summary && costs.summary.ntd_year });
      })
      .catch((err) => {
        if (ask !== invEqAsk.current) return;
        console.error('[investment] same-budget scenarios failed to load:', err);
        setInvEqError(err.message);
      });
  }, [showTransitInvestment, inv, invEq, invView, invEqRetry]);
  const changeInvTarget = (v) => { setInvPreset(null); setInvTarget(v); };
  const changeInvBoost = (v) => { setInvPreset(null); setInvBoost(v); };
  const changeInvMetric = (m) => {
    if (m === invMetric) return;
    setInvPreset(null);
    setInvMetric(m);
  };
  // The sliders take the preset's share and boost, so moving one carries on from the preset.
  const applyInvPreset = (p) => {
    setInvPreset(p.key);
    setInvTarget(Math.round(p.cum));
    setInvBoost(p.boost);
    setInvMetric('reach');
  };
  // Draggable like the Congestion Pricing panel; it starts at the left edge, clear of
  // that panel's default spot at x 320.
  const [invPanelPosition, setInvPanelPosition] = useState({ x: 10, y: 66 });
  const [isInvDragging, setIsInvDragging] = useState(false);
  const invDragStartRef = useRef({ x: 0, y: 0 });
  const invPanelRef = useRef(null);
  const handleInvDragStart = (e) => {
    if (e.target.closest('input') || e.target.closest('button') || e.target.closest('summary')) return;
    // A press on the panel's scrollbar, or a list's inside it, scrolls it.
    if (e.target.clientWidth > 0 && e.nativeEvent.offsetX > e.target.clientWidth) return;
    setIsInvDragging(true);
    invDragStartRef.current = { x: e.clientX - invPanelPosition.x, y: e.clientY - invPanelPosition.y };
  };
  useEffect(() => {
    if (!isInvDragging) return undefined;
    const move = (e) => setInvPanelPosition(onMap(invPanelRef.current, {
      x: e.clientX - invDragStartRef.current.x,
      y: e.clientY - invDragStartRef.current.y,
    }));
    const end = () => setIsInvDragging(false);
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', end);
    return () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', end);
    };
  }, [isInvDragging]);
  const invPlace = {
    left: invPanelPosition.x,
    top: invPanelPosition.y,
    cursor: isInvDragging ? 'grabbing' : 'grab',
  };
  const invOrder = useMemo(() => (inv ? orderLines(inv.lines, invMetric) : null), [inv, invMetric]);
  const invSel = useMemo(() => (inv ? selectLines(inv.lines, invOrder, invTarget) : null), [inv, invOrder, invTarget]);
  const invApplied = invEq && invPreset ? invEq.presets.find((p) => p.key === invPreset) : null;
  const invPick = invApplied || invSel; // { sel, cum }
  const invStats = useMemo(
    () => (invApplied ? invApplied.stats
      : inv && invSel ? summarizeInv(inv.cells, inv.lines, invSel.sel, invBoost, inv.meta.total_weekday_trips) : null),
    [inv, invSel, invBoost, invApplied]);
  const invShown = !!(showTransitInvestment && inv && invPick && invStats);
  useKeepOnMap(invPanelRef, setInvPanelPosition, invShown);
  useEffect(() => {
    const IDS = ['inv-hex-layer', 'inv-routes-layer', 'inv-s5-routes'];
    if (!showTransitInvestment || !inv || !invPick) {
      setLayers(prev => prev.filter(l => l && !IDS.includes(l.id)));
      return;
    }
    // Scenario 5 shades hexagons by low-income share only: the explore view's added trips are not its.
    const modelled = invView === 'modelled';
    // Not pickable under the Network Editor: a click on bare map there clears its selection.
    const hexLayer = modelled
      ? getInvestmentHexLayer(inv.cells, 'lines', inv.lines, NO_LINES, 0, { pickable: !showNetworkEditor })
      : getInvestmentHexLayer(inv.cells, invMode, inv.lines, invPick.sel, invBoost, { pickable: !showNetworkEditor });
    const routeLayer = modelled
      ? getUpgradedLinesLayer(inv.routes, inv.upgrades)
      : getInvestmentRouteLayer(inv.routes, inv.lines, invPick.sel, invBoost);
    // Hexagons first so everything else draws on top of them; selected lines last, above the hexagons.
    setLayers(prev => [
      ...(hexLayer ? [hexLayer] : []),
      ...prev.filter(l => l && !IDS.includes(l.id)),
      ...(routeLayer ? [routeLayer] : []),
    ]);
  }, [showTransitInvestment, inv, invPick, invMode, invBoost, invView, showNetworkEditor]); // eslint-disable-line

  // Income overlay: low-income household share per hexagon (res 8, children of the res-7 TAZs), from a static file.
  const incomeDataRef = useRef(null);
  const incomeAsk = useRef(0);
  const [incomeMeta, setIncomeMeta] = useState(null);
  const [incomeError, setIncomeError] = useState(null);
  useEffect(() => {
    const ID = 'income-hex-layer';
    const ask = ++incomeAsk.current;
    if (!showIncome) {
      setLayers(prev => prev.filter(l => l && l.id !== ID));
      return;
    }
    const apply = (cells) => {
      const layer = getIncomeHexLayer(cells, { pickable: !showNetworkEditor });
      // Insert first so the transit lines and stops draw on top of it.
      setLayers(prev => [...(layer ? [layer] : []), ...prev.filter(l => l && l.id !== ID)]);
    };
    if (incomeDataRef.current) {
      apply(incomeDataRef.current);
      return;
    }
    setIncomeError(null);
    fetchStaticJson('/data/income/hex_income_res8.json').then((doc) => {
      if (!incomeDataRef.current) {
        incomeDataRef.current = Object.entries(doc.hex).map(([h3id, v]) => ({
          h3id, taz: v[0], pop: v[1], hh: v[2], low: v[3], share: v[4], median: v[5],
          polygon: h3CellToBoundary(h3id).map(([lat, lng]) => [lng, lat]),
        }));
        setIncomeMeta(doc.meta);
      }
      if (ask === incomeAsk.current) apply(incomeDataRef.current);
    }).catch((err) => {
      if (ask !== incomeAsk.current) return;
      console.error('[income] failed to load hexagon income file:', err);
      setIncomeError(err.message);
    });
  }, [showIncome, showNetworkEditor]); // eslint-disable-line

  // Bus stops are drawn only when zoomed in; the boolean flips the effect once at the threshold.
  const busStopsOn = !!viewState && viewState.zoom >= 15;

  // Transit (Public Transport) layer — fetch GeoJSON + metadata once and cache them
  const transitAsk = useRef(0);
  const [transitError, setTransitError] = useState(null);
  useEffect(() => {
    const TRANSIT_IDS = ['transit-routes', 'transit-stops'];
    const ask = ++transitAsk.current;
    if (!showPublicTransport) {
      setLayers(prev => prev.filter(l => l && !TRANSIT_IDS.includes(l.id)));
      return;
    }
    const applyLayers = (routes, stops) => {
      const routeLayer = getTransitRouteLayer(routes);
      const stopLayer = getTransitStopLayer(stops, busStopsOn);
      setLayers(prev => [
        ...prev.filter(l => l && !TRANSIT_IDS.includes(l.id)),
        ...(routeLayer ? [routeLayer] : []),
        ...(stopLayer ? [stopLayer] : []),
      ]);
    };
    if (transitDataRef.current) {
      const { routes, stops } = transitDataRef.current;
      applyLayers(routes, stops);
      return;
    }
    setTransitError(null);
    Promise.all([
      fetchStaticJson(TRANSIT_ROUTES_URL),
      fetchStaticJson('/data/transit/stops.geojson'),
      fetchStaticJson('/data/transit/routes.json'),
    ]).then(([routes, stops, routesMeta]) => {
      if (!transitDataRef.current) {
        transitDataRef.current = { routes, stops };
        const lines = (routesMeta && routesMeta.lines) || [];
        const categories = [...new Set(lines.map(l => l.category))].sort();
        const agencyNames = [...new Set(lines.map(l => l.agency))].sort();
        const hasBart = agencyNames.some(a => a.toLowerCase() === 'bart');
        const has511 = agencyNames.some(a => a.toLowerCase() !== 'bart');
        setTransitMeta({ hasBart, has511, categories, agencyNames, lines });
      }
      if (ask === transitAsk.current) applyLayers(routes, stops);
    }).catch((err) => {
      if (ask !== transitAsk.current) return;
      console.error('[transit] failed to load GeoJSON:', err);
      setTransitError(err.message);
    });
  }, [showPublicTransport, busStopsOn]); // eslint-disable-line

  return (
      <div className={`map-container ${isFullscreen ? 'fullscreen-map' : ''} ${metroLocked ? 'metro-locked' : ''}`}>
        {/* Regular search bar - hidden in shortest path mode or fullscreen */}
        {!showShortestPath && !isFullscreen && !showNetworkEditor && (
          <div className="search-bar-container">
            <div className="search-input-wrapper">
              <FaSearch className="search-icon" size={16} color="#7CC644" />
              {isDemo() ? (
                // The demo keeps the box's look but takes no input: it shows one metro.
                <input
                  type="text"
                  className="search-bar-off"
                  disabled
                  placeholder="Search by state, county, or project-specific location"
                  aria-label="Search by state, county, or project-specific location"
                />
              ) : (
                <SearchBox
                  accessToken={getMapboxToken()}
                  placeholder="Search by state, county, or project-specific location"
                  onRetrieve={(res) => {
                    const feature = res.features[0];
                    if (!feature) return;
                    const [lng, lat] = feature.geometry.coordinates;
                    setViewState((prev) => ({
                      ...prev, longitude: lng, latitude: lat, zoom: 14, transitionDuration: 1000,
                    }));
                  }}
                />
              )}
            </div>
          </div>
        )}

        {/* Shortest Path search UI */}
        {showShortestPath && (
          <div
            ref={panelRef}
            className={`shortest-path-container ${isDragging ? 'dragging' : ''} ${folded('sp')}`}
            style={{
              transform: `translate(calc(-50% + ${panelPosition.x}px), ${panelPosition.y}px)`,
              top: 0,
              cursor: isDragging ? 'grabbing' : 'grab'
            }}
            onMouseDown={handleDragStart}
          >
            <div className="shortest-path-header">
              <span>Find Shortest Path</span>
              <span className="panel-head-actions">
                {foldToggle('sp', 'Shortest Path')}
                <button className="clear-route-btn" onClick={handleCloseShortestPath} title="Close">
                  <FaTimes size={14} />
                </button>
              </span>
            </div>
            <div className="shortest-path-inputs">
              <div className="route-input-wrapper">
                <FaMapMarkerAlt className="route-icon start-icon" size={16} color="#FFFFFF" />
                {startPoint?.source === 'map' ? (
                  <div className="map-selected-point">
                    <span className="point-coords">{startPoint.name}</span>
                    <button className="clear-point-btn" onClick={() => handleClearPoint('start')} title="Clear">
                      <FaTimes size={10} />
                    </button>
                  </div>
                ) : (
                  <SearchBox
                    accessToken={getMapboxToken()}
                    placeholder={endPoint?.source === 'map' ? "Disabled - using map selection" : "Enter starting point or click map"}
                    value={startPoint?.name || ''}
                    options={{ proximity: { lng: viewState.longitude, lat: viewState.latitude } }}
                    onRetrieve={(res) => {
                      const feature = res.features[0];
                      if (feature) {
                        const [lng, lat] = feature.geometry.coordinates;
                        setStartPoint({
                          coord: [lng, lat],
                          name: feature.properties.name || feature.properties.full_address,
                          source: 'search'
                        });
                        setViewState((prev) => ({
                          ...prev,
                          longitude: lng,
                          latitude: lat,
                          zoom: 14,
                          transitionDuration: 1000
                        }));
                      }
                    }}
                  />
                )}
                {startPoint && startPoint.source === 'search' && (
                  <button className="clear-point-btn inline" onClick={() => handleClearPoint('start')} title="Clear">
                    <FaTimes size={10} />
                  </button>
                )}
              </div>
              <div className="route-input-wrapper">
                <FaFlagCheckered className="route-icon end-icon" size={16} color="#7CC644" />
                {endPoint?.source === 'map' ? (
                  <div className="map-selected-point">
                    <span className="point-coords">{endPoint.name}</span>
                    <button className="clear-point-btn" onClick={() => handleClearPoint('end')} title="Clear">
                      <FaTimes size={10} />
                    </button>
                  </div>
                ) : (
                  <SearchBox
                    accessToken={getMapboxToken()}
                    placeholder={startPoint?.source === 'map' ? "Disabled - using map selection" : "Enter destination or click map"}
                    value={endPoint?.name || ''}
                    options={{ proximity: { lng: viewState.longitude, lat: viewState.latitude } }}
                    onRetrieve={(res) => {
                      const feature = res.features[0];
                      if (feature) {
                        const [lng, lat] = feature.geometry.coordinates;
                        setEndPoint({
                          coord: [lng, lat],
                          name: feature.properties.name || feature.properties.full_address,
                          source: 'search'
                        });
                        setViewState((prev) => ({
                          ...prev,
                          longitude: lng,
                          latitude: lat,
                          zoom: 14,
                          transitionDuration: 1000
                        }));
                      }
                    }}
                  />
                )}
                {endPoint && endPoint.source === 'search' && (
                  <button className="clear-point-btn inline" onClick={() => handleClearPoint('end')} title="Clear">
                    <FaTimes size={10} />
                  </button>
                )}
              </div>
            </div>
            {/* Hint for map selection */}
            {!startPoint && !endPoint && (
              <div className="selection-hint">
                Click on map to select points, or search above
              </div>
            )}
            {/* Transport type selector */}
            <div className="transport-type-selector">
              {TRANSPORT_TYPES.map((t) => {
                const Icon = t.icon;
                const isSelected = selectedTransport === t.key;
                const isDisabled = t.disabled || !startPoint || !endPoint;
                return (
                  <button
                    key={t.key}
                    className={`transport-btn ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                    onClick={() => handleTransportSelect(t)}
                    disabled={isDisabled}
                    title={t.disabled ? 'Coming soon' : t.label}
                  >
                    <Icon size={16} />
                    <span>{t.label}</span>
                  </button>
                );
              })}
            </div>
            {selectedTransport === 'public-transport' && (routeData?.noService || routeData?.metadata?.departure) && (
              <div className="route-transit-note" aria-live="polite">
                {routeData.noService
                  ? `No transit trip found leaving at ${routeData.noService.departure.slice(11, 16)} on a weekday.`
                  : `Transit leaves at ${routeData.metadata.departure.slice(11, 16)} on a weekday.`}
              </div>
            )}
          </div>
        )}
        {/* Macro Analysis Panel */}
        {showMacroAnalysis && (
          <div
            ref={macroPanelRef}
            className={`macro-analysis-container ${isMacroDragging ? 'dragging' : ''}`}
            style={{
              transform: `translate(calc(-50% + ${macroPanelPosition.x}px), ${macroPanelPosition.y}px)`,
              top: 0,
              cursor: isMacroDragging ? 'grabbing' : 'grab'
            }}
            onMouseDown={handleMacroDragStart}
          >
            <div className="macro-analysis-header">
              <span>Comparison Analysis</span>
              <button className="clear-route-btn" onClick={handleCloseMacroAnalysis} title="Close">
                <FaTimes size={14} />
              </button>
            </div>

            {/* Serves prepared Manhattan results from /data/case-study/*.json regardless of the
                region on the map — hence the notice. */}
            {activeDataset?.kind === 'run' ? (
              <div className="macro-source-note live">
                Runs the real chain on {String(activeDataset.label).split(' — ')[0]},
                {' '}{String(activeDataset.hour).padStart(2, '0')}:00–{String((activeDataset.hour + 1) % 24).padStart(2, '0')}:00.
                Congestion pricing simulates the zone you draw (~7 s).
              </div>
            ) : (
              <div className="macro-source-note">
                Prepared Manhattan case study — illustrative results, not a pipeline run.
              </div>
            )}

            {/* Scenario Selection */}
            <div className="macro-scenario-selector">
              {SCENARIOS.map(s => (
                <button
                  key={s.key}
                  className={`macro-scenario-btn ${macroScenario === s.key ? 'selected' : ''}`}
                  onClick={() => {
                    setMacroScenario(s.key);
                    // Clear CRZ when switching away from CP
                    if (s.key !== 'congestion-pricing') setMacroCrzArea(null);
                  }}
                >
                  {s.label}
                </button>
              ))}
            </div>

            <div className="macro-section">
              <div style={{ display: activeDataset?.kind === 'run' ? 'none' : undefined }}>
              <div className="macro-section-label">Analysis Area</div>
              <div className="macro-area-row">
                {macroAnalysisArea ? (
                  <div className="macro-area-indicator">
                    <span className="macro-area-coords">
                      Polygon drawn ({macroAnalysisArea.length - 1} vertices)
                    </span>
                    <button className="macro-area-clear" onClick={() => { setMacroAnalysisArea(null); setMacroCrzArea(null); }} title="Clear">
                      <FaTimes size={10} />
                    </button>
                  </div>
                ) : (
                  <button
                    className={`macro-draw-btn ${macroDrawingMode === 'analysis-area' ? 'drawing' : ''}`}
                    onClick={() => {
                      setMacroDrawingMode(macroDrawingMode === 'analysis-area' ? 'none' : 'analysis-area');
                      setMacroDrawingVertices([]);
                    }}
                  >
                    <FaCrosshairs size={12} />
                    {macroDrawingMode === 'analysis-area'
                      ? `Drawing... (${macroDrawingVertices.length} pts)`
                      : 'Draw on Map'}
                  </button>
                )}
              </div>

              {/* Drawing action buttons */}
              {macroDrawingMode === 'analysis-area' && macroDrawingVertices.length > 0 && (
                <div className="macro-drawing-actions">
                  <button onClick={() => setMacroDrawingVertices(prev => prev.slice(0, -1))}>
                    <FaUndo size={10} /> Undo
                  </button>
                  {macroDrawingVertices.length >= 3 && (
                    <button onClick={closeMacroPolygon}>
                      <FaCheck size={10} /> Close Polygon
                    </button>
                  )}
                </div>
              )}

              {macroDrawingMode === 'analysis-area' && macroDrawingVertices.length < 3 && macroDrawingVertices.length > 0 && (
                <div className="macro-drawing-hint">Need at least 3 points</div>
              )}
              {macroDrawingMode === 'analysis-area' && macroDrawingVertices.length >= 3 && (
                <div className="macro-drawing-hint">Click first point or press Close to finish</div>
              )}
              </div>

              {/* CRZ area — only for Congestion Pricing and only after analysis area is drawn */}
              {macroScenario === 'congestion-pricing'
                && (macroAnalysisArea || activeDataset?.kind === 'run') && (
                <>
                  <div className="macro-section-label" style={{ marginTop: 8 }}>Congestion Relief Zone (CRZ)</div>
                  <div className="macro-area-row">
                    {macroCrzArea ? (
                      <div className="macro-area-indicator crz">
                        <span className="macro-area-coords">
                          Polygon drawn ({macroCrzArea.length - 1} vertices)
                        </span>
                        <button className="macro-area-clear" onClick={() => setMacroCrzArea(null)} title="Clear">
                          <FaTimes size={10} />
                        </button>
                      </div>
                    ) : (
                      <button
                        className={`macro-draw-btn crz ${macroDrawingMode === 'crz' ? 'drawing' : ''}`}
                        onClick={() => {
                          setMacroDrawingMode(macroDrawingMode === 'crz' ? 'none' : 'crz');
                          setMacroDrawingVertices([]);
                        }}
                      >
                        <FaCrosshairs size={12} />
                        {macroDrawingMode === 'crz'
                          ? `Drawing... (${macroDrawingVertices.length} pts)`
                          : 'Draw CRZ on Map'}
                      </button>
                    )}
                  </div>

                  {/* CRZ drawing action buttons */}
                  {macroDrawingMode === 'crz' && macroDrawingVertices.length > 0 && (
                    <div className="macro-drawing-actions">
                      <button onClick={() => setMacroDrawingVertices(prev => prev.slice(0, -1))}>
                        <FaUndo size={10} /> Undo
                      </button>
                      {macroDrawingVertices.length >= 3 && (
                        <button onClick={closeMacroPolygon}>
                          <FaCheck size={10} /> Close Polygon
                        </button>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Parameters */}
            <div className="macro-section">
              <div className="macro-section-label">Parameters</div>
              <div className="macro-param-grid">
                {/* Read-only on a produced dataset: the only modelled slice is the one the run was
                    built for. */}
                {activeDataset?.kind === 'run' ? (
                  <>
                    <label>Day</label>
                    <div className="macro-param-fixed">
                      {DAYS_OF_WEEK.find(d => d.key === 'weekday')?.label || 'Weekday'}
                      {activeDataset.weekday ? ` (${activeDataset.weekday})` : ''}
                    </div>
                    <label>Hour</label>
                    <div className="macro-param-fixed">
                      {String(activeDataset.hour).padStart(2, '0')}:00–
                      {String((activeDataset.hour + 1) % 24).padStart(2, '0')}:00
                      <span> — the only modelled slice</span>
                    </div>
                  </>
                ) : (
                  <>
                    <label>Day</label>
                    <select
                      value={macroParams.dayOfWeek}
                      onChange={e => setMacroParams(p => ({ ...p, dayOfWeek: e.target.value }))}
                    >
                      {DAYS_OF_WEEK.map(d => (
                        <option key={d.key} value={d.key}>{d.label}</option>
                      ))}
                    </select>

                    <label>Start Hour</label>
                    <input
                      type="number" min={0} max={23}
                      value={macroParams.timeStart}
                      onChange={e => setMacroParams(p => ({ ...p, timeStart: parseInt(e.target.value) || 0 }))}
                    />

                    <label>End Hour</label>
                    <input
                      type="number" min={0} max={24}
                      value={macroParams.timeEnd}
                      onChange={e => setMacroParams(p => ({ ...p, timeEnd: parseInt(e.target.value) || 0 }))}
                    />
                  </>
                )}

                {macroScenario === 'congestion-pricing' && (
                  <>
                    <label>VTTS ($/hr)</label>
                    <input
                      type="number" step="0.1" min={0}
                      value={macroParams.vtts}
                      onChange={e => setMacroParams(p => ({ ...p, vtts: parseFloat(e.target.value) || 0 }))}
                    />
                  </>
                )}

                {macroScenario === 'school-zones' && (
                  <>
                    <label>School Start</label>
                    <input
                      type="time"
                      value={macroParams.schoolStart}
                      onChange={e => setMacroParams(p => ({ ...p, schoolStart: e.target.value }))}
                    />
                    <label>School End</label>
                    <input
                      type="time"
                      value={macroParams.schoolEnd}
                      onChange={e => setMacroParams(p => ({ ...p, schoolEnd: e.target.value }))}
                    />
                    <label>Zone Radius (m)</label>
                    <input
                      type="number" min={50} step={50}
                      value={macroParams.zoneRadius}
                      onChange={e => setMacroParams(p => ({ ...p, zoneRadius: parseInt(e.target.value) || 200 }))}
                    />
                  </>
                )}
              </div>
            </div>

            {/* CRZ validation warning */}
            {isCrzOutside && (
              <div className="macro-warning">
                CRZ must be fully inside the analysis area
              </div>
            )}

            {/* Run Button */}
            <button
              className="macro-run-btn"
              onClick={handleRunMacroAnalysis}
              disabled={macroRunning || (activeDataset?.kind !== 'run' && (!macroAnalysisArea || isCrzOutside))}
            >
              <FaPlay size={12} />
              {macroRunning ? 'Running the simulation…' : 'Run Analysis'}
            </button>

            {macroRunResult && (
              <div className="macro-run-result">
                {macroRunResult.error ? (
                  <div className="macro-run-error">{macroRunResult.error}</div>
                ) : macroRunResult.kind === 'zone' ? (() => {
                  const d = macroRunResult.data;
                  const f = (v) => (v == null ? '—' : `${v > 0 ? '+' : ''}${v}%`);
                  return (
                    <>
                      <div className="macro-run-title">
                        Priced zone · {d.zone.edges} segments · +{Math.round(d.zone.penaltyS)} s each
                      </div>
                      <div className="macro-run-grid">
                        <div><span>{f(d.totals.inZoneVehicles.pct)}</span>vehicles in zone</div>
                        <div><span>{f(d.totals.outsideVehicles.pct)}</span>vehicles outside</div>
                        <div><span>{d.totals.congestedEdges.b}</span>congested (was {d.totals.congestedEdges.a})</div>
                        <div><span>{d.elapsedS}s</span>simulation time</div>
                      </div>
                      <div className="macro-run-note">
                        The charge is applied to every segment inside the zone, converted to
                        time at ${d.zone.vtts}/h. Read this as drivers minimising time spent
                        in the zone, not as toll compliance.
                      </div>
                    </>
                  );
                })() : (() => {
                  const t = macroRunResult.data.totals;
                  const f = (v) => (v == null ? '—' : `${v > 0 ? '+' : ''}${v}%`);
                  return (
                    <>
                      <div className="macro-run-title">School zones vs baseline</div>
                      <div className="macro-run-grid">
                        <div><span>{f(t.vmtMi.pct)}</span>vehicle-miles</div>
                        <div><span>{f(t.congestedEdges.pct)}</span>congested segments</div>
                        <div><span>{t.relieved}</span>segments relieved</div>
                        <div><span>{t.worsened}</span>segments worsened</div>
                      </div>
                    </>
                  );
                })()}
              </div>
            )}
          </div>
        )}

        {/* Macro Analysis Results Modal */}
        {showMacroResultModal && macroResultData && (
          <div className="macro-result-overlay" onClick={() => setShowMacroResultModal(false)}>
            <div className="macro-result-modal" onClick={e => e.stopPropagation()}>
              <div className="macro-result-header">
                <span>Analysis Results Summary</span>
                <button onClick={() => setShowMacroResultModal(false)}>
                  <FaTimes size={14} />
                </button>
              </div>

              <div className="macro-result-body">
                {macroResultData.level1?.summary && (
                  <div className="macro-result-stats">
                    <StatCard title="Total VMT" value={macroResultData.level1.summary.vmt?.cp} change={macroResultData.level1.summary.vmt?.pct} unit="mi" />
                    <StatCard title="Avg V/C Ratio" value={macroResultData.level1.summary.avg_vc?.cp} change={macroResultData.level1.summary.avg_vc?.pct} />
                    <StatCard title="Avg Congested Edges" value={macroResultData.level1.summary.avg_congested?.cp} change={macroResultData.level1.summary.avg_congested?.pct} />
                  </div>
                )}
              </div>

              <button className="macro-result-open-full" onClick={handleOpenFullAnalysis}>
                <FaExternalLinkAlt size={12} />
                Open Full Analysis
              </button>
            </div>
          </div>
        )}

        {/* School Zones Panel (draggable) */}
        {showSchoolZones && (
          <div
            ref={szPanelRef}
            className={`sz-panel ${isSzDragging ? 'dragging' : ''} ${folded('sz')}`}
            style={{
              left: szPanelPosition.x,
              top: szPanelPosition.y,
              cursor: isSzDragging ? 'grabbing' : 'grab'
            }}
            onMouseDown={handleSzDragStart}
          >
            <div className="sz-panel-header">
              <span>School Zones</span>
              <span className="panel-head-actions">
                {foldToggle('sz', 'School Zones')}
                <button
                  className="clear-route-btn"
                  onClick={() => {
                    setSzShowAffected(false); setSzShowHeatmap(false); setSzShowNames(false);
                    clearScenarioResult();
                    if (onCloseSchoolZones) onCloseSchoolZones();
                  }}
                  title="Close"
                >
                  <FaTimes size={14} />
                </button>
              </span>
            </div>
            {szLoading ? (
              <div className="sz-loading">Loading data...</div>
            ) : (
              <div className="sz-toggles">
                <div className="sz-info">
                  {szData ? `${szData.schools.length} schools` : '...'} | 200m radius zones
                </div>
                {caseStudyRegion && (
                  <label className="sz-toggle-row">
                    <input type="checkbox" checked={szShowAffected} onChange={() => setSzShowAffected(prev => !prev)} />
                    <span>Affected Roads</span>
                    <span className="sz-toggle-desc">(speed-limited segments)</span>
                  </label>
                )}
                {caseStudyRegion && (
                  <label className="sz-toggle-row">
                    <input type="checkbox" checked={szShowHeatmap} onChange={() => setSzShowHeatmap(prev => !prev)} />
                    <span>V/C Ratio Change</span>
                    <span className="sz-toggle-desc">(8AM-5PM average)</span>
                  </label>
                )}
                <label className="sz-toggle-row">
                  <input type="checkbox" checked={szShowNames} onChange={() => setSzShowNames(prev => !prev)} />
                  <span>School Names</span>
                  <span className="sz-toggle-desc">(labels on map)</span>
                </label>
                {pbSz ? (
                  <PrebakedSzSection
                    edit={pbSz}
                    region={metroName}
                    active={pbChoice?.id === pbSz.id}
                    result={pbChoice?.id === pbSz.id ? pbResult : null}
                    onShow={() => choosePrebaked('sz', pbSz.id)} />
                ) : !prebakedOnly && renderScenarioControls('school-zones')}
                {caseStudyRegion && (
                  <button className="case-study-report" onClick={() => openBakedReport('school-zones')}>
                    <FaExternalLinkAlt size={10} /> Open full comparison report
                  </button>
                )}
                {caseStudyRegion && szShowHeatmap && (
                  <div className="sz-legend">
                    <div className="sz-legend-title">V/C Ratio Change (Plain → SZ)</div>
                    <div className="sz-legend-item"><span className="sz-legend-color" style={{background:'#006400'}}></span> Relieved (&Delta;V/C &lt; -0.1)</div>
                    <div className="sz-legend-item"><span className="sz-legend-color" style={{background:'#90ee90'}}></span> Slightly relieved (-0.1 to -0.01)</div>
                    <div className="sz-legend-item"><span className="sz-legend-color" style={{background:'#969696'}}></span> Minimal change (&plusmn;0.01)</div>
                    <div className="sz-legend-item"><span className="sz-legend-color" style={{background:'#ffa0a0'}}></span> Slightly worsened (0.01 to 0.1)</div>
                    <div className="sz-legend-item"><span className="sz-legend-color" style={{background:'#8b0000'}}></span> Worsened (&Delta;V/C &gt; 0.1)</div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {showCongestionPricing && (
          <div
            ref={cpPanelRef}
            className={`cp-panel ${prebakedOnly && prebaked?.cp ? 'pb-wide' : ''} ${showDayStrip ? 'pb-above-strip' : ''} ${isCpDragging ? 'dragging' : ''} ${folded('cp')}`}
            style={{
              left: cpPanelPosition.x,
              top: cpPanelPosition.y,
              cursor: isCpDragging ? 'grabbing' : 'grab'
            }}
            onMouseDown={handleCpDragStart}
          >
            <div className="cp-panel-header">
              <span>Congestion Pricing</span>
              <span className="panel-head-actions">
                {foldToggle('cp', 'Congestion Pricing')}
                <button
                  className="clear-route-btn"
                  onClick={() => {
                    setCpShowTolled(false); setCpShowHeatmap(false); setCpShowReroute(false);
                    setMacroCrzArea(null);
                    setMacroDrawingMode('none');
                    setMacroDrawingVertices([]);
                    clearScenarioResult();
                    if (onCloseCongestionPricing) onCloseCongestionPricing();
                  }}
                  title="Close"
                >
                  <FaTimes size={14} />
                </button>
              </span>
            </div>
            {prebakedOnly ? (
              <div className="cp-toggles">
                {prebaked?.cp ? (
                  <PrebakedCpSection
                    cp={prebaked.cp}
                    hour={cpHour}
                    region={metroName}
                    choice={pbChoice?.panel === 'cp' ? pbChoice : null}
                    result={pbChoice?.panel === 'cp' ? pbResult : null}
                    day={pbDay}
                    bands={{ on: pbBandsOn, state: pbBands }}
                    onBands={setPbBandsOn}
                    serviceLevel={serviceLevel}
                    onServiceLevel={onServiceLevel}
                    levelRanks={pbRanks}
                    onChoose={(id, against) => choosePrebaked('cp', id, against)} />
                ) : (
                  <div className="cp-info">
                    {prebaked ? 'No charging zone was computed for this metro.' : 'Loading…'}
                  </div>
                )}
              </div>
            ) : !caseStudyRegion ? (
              <div className="cp-toggles">
                <div className="cp-info">
                  No fixed charging zone here — draw one and it is simulated for real.
                </div>
                {renderScenarioControls('congestion-pricing')}
              </div>
            ) : cpLoading ? (
              <div className="cp-loading">Loading data...</div>
            ) : (
              <div className="cp-toggles">
                <div className="cp-info">
                  {cpData ? `${cpData.tolledEdges.length} tolled segments` : '...'} | MTA CBD geofence
                </div>
                <button className="case-study-report" onClick={() => openBakedReport('congestion-pricing')}>
                  <FaExternalLinkAlt size={10} /> Open full comparison report
                </button>
                <label className="cp-toggle-row">
                  <input type="checkbox" checked={cpShowTolled} onChange={() => setCpShowTolled(prev => !prev)} />
                  <span>Tolled Roads</span>
                  <span className="cp-toggle-desc">(segments inside CRZ)</span>
                </label>
                {caseStudyRegion && (
                  <label className="cp-toggle-row">
                    <input type="checkbox" checked={cpShowHeatmap} onChange={() => setCpShowHeatmap(prev => !prev)} />
                    <span>V/C Ratio Change</span>
                    <span className="cp-toggle-desc">(5AM-9PM average)</span>
                  </label>
                )}
                {cpShowHeatmap && (
                  <div className="cp-legend">
                    <div className="cp-legend-title">V/C Ratio Change (Plain &rarr; CP)</div>
                    <div className="cp-legend-item"><span className="cp-legend-color" style={{background:'#006400'}}></span> Relieved (&Delta;V/C &lt; -0.1)</div>
                    <div className="cp-legend-item"><span className="cp-legend-color" style={{background:'#90ee90'}}></span> Slightly relieved (-0.1 to -0.01)</div>
                    <div className="cp-legend-item"><span className="cp-legend-color" style={{background:'#969696'}}></span> Minimal change (&plusmn;0.01)</div>
                    <div className="cp-legend-item"><span className="cp-legend-color" style={{background:'#ffa0a0'}}></span> Slightly worsened (0.01 to 0.1)</div>
                    <div className="cp-legend-item"><span className="cp-legend-color" style={{background:'#8b0000'}}></span> Worsened (&Delta;V/C &gt; 0.1)</div>
                  </div>
                )}
                <label className="cp-toggle-row">
                  <input type="checkbox" checked={cpShowReroute} onChange={() => setCpShowReroute(prev => !prev)} />
                  <span>Rerouted Trips</span>
                  <span className="cp-toggle-desc">(sampled, {cpData?.reroutedTrips?.stats.hour})</span>
                </label>
                {cpShowReroute && cpData?.reroutedTrips && (
                  <div className="cp-legend">
                    <div className="cp-legend-title">Same vehicle, before &rarr; after</div>
                    <div className="cp-legend-item"><span className="cp-legend-color" style={{background:'#e74c3c'}}></span> Original route</div>
                    <div className="cp-legend-item"><span className="cp-legend-color" style={{background:'#3498db'}}></span> Rerouted under CP</div>
                    <div className="cp-legend-note">
                      Stratified sample: 40 trips from each of 5 CRZ exposure-drop bands ({cpData.reroutedTrips.trips.length} shown).
                      Stats below cover all {cpData.reroutedTrips.stats.total_trips.toLocaleString()} trips in the hour.
                    </div>
                    <div className="cp-legend-stat">
                      <b>{cpData.reroutedTrips.stats.rerouted_pct}%</b> of trips rerouted
                    </div>
                    <div className="cp-legend-stat">
                      CRZ edges per trip: <b>{cpData.reroutedTrips.stats.mean_crz_edges_plain} &rarr; {cpData.reroutedTrips.stats.mean_crz_edges_cp}</b> ({cpData.reroutedTrips.stats.crz_exposure_pct_change}%)
                    </div>
                    <div className="cp-legend-stat">
                      <b>{Math.round(100 * cpData.reroutedTrips.stats.exposure_reduced_trips / cpData.reroutedTrips.stats.crz_bound_trips)}%</b> of CRZ-bound trips cut their exposure
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {showNetworkEditor && prebakedOnly && (
          <PrebakedEditsPanel
            panelRef={nePanelRef}
            position={nePanelPosition}
            isDragging={isNeDragging}
            onDragStart={handleNeDragStart}
            onClose={() => { clearPrebaked(); if (onCloseNetworkEditor) onCloseNetworkEditor(); }}
            region={metroName}
            loading={!prebaked}
            edits={pbNeEdits}
            choiceId={pbChoice?.panel === 'ne' ? pbChoice.id : null}
            result={pbChoice?.panel === 'ne' ? pbResult : null}
            onChoose={(id) => choosePrebaked('ne', id)}
            onClear={clearPrebaked} />
        )}

        {showNetworkEditor && !prebakedOnly && liveSim && (
          <NetworkEditorPanel
            panelRef={nePanelRef}
            position={nePanelPosition}
            isDragging={isNeDragging}
            onDragStart={handleNeDragStart}
            onClose={() => { resetNe(); if (onCloseNetworkEditor) onCloseNetworkEditor(); }}
            manifest={neManifest}
            hasMetrics={!!(neManifest || neRunResult?.edges || neDemandBaseline?.edges)}
            cbsa={neCbsa}
            region={neManifest?.region || metroName || (neCbsa && `CBSA ${neCbsa}`)}
            loading={neLoading || (!!neCbsa && !neWarm && !neServiceFailed)}
            loadError={neServiceFailed && !neEdges ? metroStatus.error : null}
            selected={neSelected}
            onClearSelection={clearNeSelection}
            summary={neRunResult?.summary || neDemandBaseline?.summary || neSummary}
            baseSummary={neDemandBaseline?.summary || neSummary}
            edits={neEdits}
            hasEdits={neHasEdits}
            running={neRunning}
            runError={neRunError}
            runResult={neRunResult}
            costModel={costModel}
            demand={neDemand}
            demandReport={neDemandReport}
            demandResult={neDemandBaseline}
            demandRunning={neDemandRunning}
            demandError={neDemandError}
            demandScale={neManifest?.demand_scale ?? 1}
            bakedTrips={neBakedTrips}
            demandResetKey={neDemandResetKey}
            flows={neFlowSummary}
            flowsOn={neShowFlows}
            onToggleFlows={setNeShowFlows}
            flowMode={neFlowMode}
            onFlowMode={setNeFlowMode}
            flowFade={neFlowFade}
            onFlowFade={setNeFlowFade}
            signalsOn={neShowSignals}
            signalsAutoHidden={neSignalsAutoHidden}
            onToggleSignals={setNeShowSignals}
            onApplyDemand={applyNeDemand}
            safetyJob={neSafetyJob}
            safetyJobOwner={neJobOwner}
            onDemandReport={() => openNeReport(neDemandBaseline)}
            onDemandJson={() => downloadNeReport(neDemandBaseline)}
            onCloseRoad={closeNeRoad}
            onReopen={reopenNeRoad}
            nameOf={nameOfEdge}
            onChangeAttrs={changeNeAttrs}
            onStageSignal={stageNeSignal}
            onStageProgram={stageNeProgram}
            onExecute={executeNeEdits}
            /* Wrapped, not by reference: a MouseEvent arriving as `result` would defeat the default parameter. */
            onReport={() => openNeReport()}
            onResetEdits={resetNeEdits}
            onResetBaseline={resetNeBaseline}
          />
        )}

        <div className="map-controls">
          <div ref={hexControlsRef} style={{ display: 'none' }}>
            <button className="map-control-button" title="Hexagon Grid Controls">
              <FaDrawPolygon size={18} color="#666" />
            </button>
            <div className={`hexagon-controls ${showHexControls ? 'visible' : ''}`}>
              <div className="size-control">
                <label>Hexagon Size</label>
                <input 
                  type="range" 
                  min="6" 
                  max="9" 
                  value={hexResolution} 
                  onChange={handleHexSizeChange}
                />
                <div className="size-value">{getCurrentSizeLabel()}</div>
              </div>
              {hexSuppressed && (
                <div className="hex-hint">
                  Too many cells at this zoom — zoom in to place the grid.
                  Cells you already selected stay selected.
                </div>
              )}
              <div className="button-row">
                <button className="clear-button" onClick={clearSelection}>
                  <FaTrash size={14} />
                  Clear Selection
                </button>
                <button className="clear-button" onClick={() => setShowHexagons(false)}>
                  Hide Grid
                </button>
              </div>
            </div>
          </div>
          
          <button className="map-control-button" onClick={() => {toggleFullscreen(); toggleSidebar();}}>
            <FaExpand size={18} color="#7CC644" />
          </button>

          {/* A demo host shows one metro: nothing to choose. */}
          {!isDemo() && (
            <div ref={datasetPanelRef}>
              <button
                className={`map-control-button ${showDatasetPanel ? 'active' : ''}`}
                onClick={() => setShowDatasetPanel(!showDatasetPanel)}
                title="Data source"
              >
                <FaLayerGroup size={18} color="#7CC644" />
              </button>
              <div className={`dataset-panel ${showDatasetPanel ? 'visible' : ''}`}>
                <div className="dataset-panel-title">Data source</div>
                {datasets.map((ds) => (
                  <button
                    key={ds.id}
                    className={`dataset-option ${activeDataset?.id === ds.id ? 'active' : ''}`}
                    onClick={() => { onSelectDataset && onSelectDataset(ds); setShowDatasetPanel(false); }}
                  >
                    <span className="dataset-option-label">{ds.label}</span>
                    {publishError(ds) ? (
                      <span className="dataset-option-sub publish-failed">
                        No traffic layer: {publishError(ds)}
                      </span>
                    ) : ds.sublabel && (
                      <span className="dataset-option-sub">{ds.sublabel}</span>
                    )}
                    <span className={`dataset-option-badge ${ds.kind}`}>
                      {ds.kind === 'run' ? 'V/C · simulated'
                        : ds.kind === 'editable' ? 'editable · no V/C layer' : 'sample'}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <button
            className="map-control-button"
            disabled={metroLocked}
            onClick={() => setViewState(vs => ({ ...vs, zoom: Math.min((vs.zoom || 11) + 1, 20) }))}
            title="Zoom in"
          >
            <FaSearchPlus size={18} color="#7CC644" />
          </button>
          <button
            className="map-control-button"
            disabled={metroLocked}
            onClick={() => setViewState(vs => ({ ...vs, zoom: Math.max((vs.zoom || 11) - 1, 2) }))}
            title="Zoom out"
          >
            <FaSearchMinus size={18} color="#7CC644" />
          </button>
        </div>
        {/* Holding a metro is not a background task: nothing on the map means
            anything until the service can answer a /run, so the wait is the
            screen. The picker above it stays live to pick a different metro. */}
        {metroLocked && (
          <div className="metro-overlay">
            <div className="metro-overlay-card">
              {metroStatus.status === 'failed' ? (
                <>
                  <div className="metro-overlay-title">
                    {metroName || metroStatus.cbsa} could not be loaded
                  </div>
                  <div className="metro-progress-error">{metroStatus.error}</div>
                  <button className="metro-overlay-retry" onClick={onRetryMetro}>
                    Retry
                  </button>
                </>
              ) : (
                <>
                  <div className="metro-overlay-title">
                    Preparing {metroName || metroStatus.cbsa}
                  </div>
                  <div className="metro-overlay-sub">
                    {METRO_STEP_NOTE[metroStep] || 'requesting the metro'}
                    {metroEta(metroEdges) ? ` · ${metroEta(metroEdges)}` : ''}
                  </div>
                  <div className="metro-overlay-bar">
                    <div
                      className="metro-overlay-bar-fill"
                      style={{ width: `${Math.max(0, metroStep) / metroSteps.length * 100}%` }}
                    />
                  </div>
                  {metroSteps.map((label, i) => (
                    <div
                      key={label}
                      className={`metro-progress-step ${i < metroStep ? 'done' : i === metroStep ? 'now' : ''}`}
                    >
                      <span className="metro-progress-dot">
                        {i < metroStep ? '✓' : i === metroStep ? <FaSpinner className="ne-spin" size={9} /> : ''}
                      </span>
                      {label}
                    </div>
                  ))}
                  <div className="metro-overlay-foot">
                    {metroEdges ? `${metroEdges.toLocaleString()} roads · ` : ''}
                    the map and its tools unlock when
                    {metroStatus.publish?.status === 'pending'
                      ? ' its traffic layer is loaded' : ' the baseline is ready'}
                  </div>
                </>
              )}
            </div>
          </div>
        )}
        {showDayStrip && (
          <DayStrip
            hours={activeDataset.hours}
            value={viewHour}
            onChange={(h) => onSelectHour && onSelectHour(h)}
            lockNote={hourLock !== null ? `School zones were computed for ${hourSpan(hourLock)} only.` : null}
            windows={prebaked?.cp?.toll_windows_h || null}
            entries={hourLock === null ? stripEntries : null} />
        )}
        {/* Labelled with the dataset: V/C and the sample "Volume" are different quantities
            on different scales. */}
        {trafficData && Object.keys(trafficData).length > 0 && !showNetworkEditor && (
          <div className={`traffic-legend ${folded('traffic')}`}>
            <div className="traffic-legend-title panel-titlebar">
              <span>{activeDataset?.metric === 'vc' ? 'Volume / Capacity' : 'Avg volume (sample)'}</span>
              {foldToggle('traffic', 'traffic legend')}
            </div>
            {(activeDataset?.metric === 'vc' ? LOS_LEGEND : VOLUME_LEGEND).map((s) => (
              <div className="traffic-legend-row" key={s.grade || s.label}>
                <span
                  className="traffic-legend-swatch"
                  style={{ background: `rgb(${s.color[0]},${s.color[1]},${s.color[2]})` }}
                />
                {s.grade ? (
                  <span className="traffic-legend-los">
                    <b>LOS {s.grade}</b>
                    <span className="traffic-legend-range">{s.range}</span>
                    <span className="traffic-legend-desc">{s.desc}</span>
                  </span>
                ) : (
                  <span>{s.label}</span>
                )}
              </div>
            ))}
            <div className="traffic-legend-row">
              <span className="traffic-legend-swatch" style={{ background: 'rgba(90,90,90,0.35)' }} />
              <span>no road</span>
            </div>
            {multiHour && trafficHour !== null ? (
              <div className="traffic-legend-foot">
                {hourSpan(trafficHour)}{activeDataset.demandScale ? ` · demand ×${activeDataset.demandScale}` : ''}
              </div>
            ) : activeDataset?.sublabel && (
              <div className="traffic-legend-foot">{activeDataset.sublabel}</div>
            )}
          </div>
        )}

        {/* One piece of state, not two booleans: a polygon and a set of hexagons are
            different questions, so choosing one drops the other's selection. */}
        {showTrafficPanel && (
          <div
            ref={tdPanelRef}
            className={`td-panel ${isTdDragging ? 'dragging' : ''} ${folded('td')}`}
            style={{
              left: tdPanelPosition.x,
              top: tdPanelPosition.y,
              cursor: isTdDragging ? 'grabbing' : 'grab'
            }}
            onMouseDown={handleTdDragStart}
          >
            <div className="td-panel-header">
              <span>Traffic Density</span>
              <span className="panel-head-actions">
                {foldToggle('td', 'Traffic Density')}
                <button className="clear-route-btn" onClick={closeTrafficPanel} title="Close">
                  <FaTimes size={14} />
                </button>
              </span>
            </div>

            <div className="td-modes">
              <button
                className={`td-mode ${tdMode === 'area' ? 'active' : ''}`}
                onClick={() => selectTdMode('area')}
              >
                <FaDrawPolygon size={12} /> Draw an area
              </button>
              <button
                className={`td-mode ${tdMode === 'hex' ? 'active' : ''}`}
                onClick={() => selectTdMode('hex')}
              >
                <FaLayerGroup size={12} /> Pick hexagons
              </button>
            </div>

            {tdMode === 'area' && (
              <div className="td-body">
                {tdArea ? (
                  <div className="macro-area-indicator">
                    <span className="macro-area-coords">Area drawn ({tdArea.length - 1} vertices)</span>
                    <button
                      className="macro-area-clear"
                      onClick={() => { setTdArea(null); onTrafficResult && onTrafficResult(null); }}
                      title="Clear"
                    >
                      <FaTimes size={10} />
                    </button>
                  </div>
                ) : (
                  <button
                    className={`macro-draw-btn ${macroDrawingMode === 'traffic-area' ? 'drawing' : ''}`}
                    onClick={() => {
                      setMacroDrawingMode(macroDrawingMode === 'traffic-area' ? 'none' : 'traffic-area');
                      setMacroDrawingVertices([]);
                    }}
                  >
                    <FaCrosshairs size={12} />
                    {macroDrawingMode === 'traffic-area'
                      ? `Drawing... (${macroDrawingVertices.length} pts)`
                      : 'Draw area on map'}
                  </button>
                )}
                {macroDrawingMode === 'traffic-area' && macroDrawingVertices.length > 0 && (
                  <div className="macro-drawing-actions">
                    <button onClick={() => setMacroDrawingVertices(prev => prev.slice(0, -1))}>
                      <FaUndo size={10} /> Undo
                    </button>
                    {macroDrawingVertices.length >= 3 && (
                      <button onClick={closeMacroPolygon}><FaCheck size={10} /> Close</button>
                    )}
                  </div>
                )}
              </div>
            )}

            {tdMode === 'hex' && (
              <div className="td-body">
                <div className="td-hint">
                  Click cells on the map to select them.
                  {selectedHexagons.size > 0 ? ` ${selectedHexagons.size} selected.` : ''}
                </div>
                <div className="size-control">
                  <label>Hexagon size</label>
                  <input type="range" min="6" max="9" value={hexResolution} onChange={handleHexSizeChange} />
                  <div className="size-value">{getCurrentSizeLabel()}</div>
                </div>
                {hexSuppressed && (
                  <div className="hex-hint">
                    Too many cells at this zoom — zoom in to place the grid.
                    Cells you already selected stay selected.
                  </div>
                )}
                <button className="clear-button" onClick={clearSelection}>
                  <FaTrash size={12} /> Clear selection
                </button>
              </div>
            )}

            {tdMode && (
              <button
                className="macro-run-btn td-run"
                disabled={tdMode === 'area' ? !tdArea : selectedHexagons.size === 0}
                onClick={runTrafficDensity}
              >
                <FaPlay size={12} /> Show density
              </button>
            )}
          </div>
        )}

        {/* Overlay legends stack above the route's conditions, clear of the map controls. */}
        <div className="map-corner-br">
          {showIncome && (
            <div className={`income-legend ${folded('income')}`}>
              <div className="transit-legend-title panel-titlebar">
                <span>Low-income households</span>
                {foldToggle('income', 'Low-income households legend')}
              </div>
              {incomeMeta ? (
                <>
                  <div className="income-legend-bar"></div>
                  <div className="income-legend-scale"><span>0%</span><span>40%</span><span>80%+</span></div>
                  <div className="transit-legend-agencies">
                    Households below 80% of the Bay Area median household income
                    (under ${incomeMeta.low_income_below.toLocaleString()}), from ACS 2019–23 block groups split to hexes by area.
                  </div>
                </>
              ) : (
                <div className={incomeError ? 'overlay-legend-error' : 'overlay-legend-note'} aria-live="polite">
                  {incomeError
                    ? 'Income data could not be loaded. Turn the layer off and on to try again.'
                    : 'Loading…'}
                </div>
              )}
            </div>
          )}
          {showPublicTransport && (
            <div className={`transit-legend ${folded('transit')}`}>
              <div className="transit-legend-title panel-titlebar">
                <span>Public Transport</span>
                {foldToggle('transit', 'Public Transport legend')}
              </div>
              {transitMeta && transitMeta.categories.includes('rail') && (
                <div className="transit-legend-item">
                  <span className="transit-legend-line transit-legend-rail"></span>
                  Rail
                </div>
              )}
              {transitMeta && transitMeta.categories.includes('bus') && (
                <div className="transit-legend-item">
                  <span className="transit-legend-line transit-legend-bus"></span>
                  Bus
                </div>
              )}
              {transitMeta && transitMeta.categories.includes('ferry') && (
                <div className="transit-legend-item">
                  <span className="transit-legend-line transit-legend-ferry"></span>
                  Ferry
                </div>
              )}
              {transitMeta && transitMeta.categories.includes('rail') && (
                <div className="transit-legend-item">
                  <span className="transit-legend-stop transit-legend-stop-rail"></span>
                  Rail station
                </div>
              )}
              {transitMeta && (
                <div className="transit-legend-agencies">
                  {transitMeta.agencyNames.length} agencies · lines use each route's colour
                </div>
              )}
              <div className="transit-legend-routing">Trip planning covers Muni and BART only.</div>
              {!transitMeta && (
                <div className={transitError ? 'overlay-legend-error' : 'overlay-legend-note'} aria-live="polite">
                  {transitError
                    ? 'Transit data could not be loaded. Turn the layer off and on to try again.'
                    : 'Loading…'}
                </div>
              )}
              {transitMeta && transitMeta.hasBart && (
                <div className="transit-attribution">
                  <a href="https://www.bart.gov/schedules" target="_blank" rel="noopener noreferrer">
                    BART schedules: bart.gov
                  </a>
                </div>
              )}
            </div>
          )}
          {routeSummary && !showNetworkEditor && (
            <div className={`route-conditions ${folded('route')}`}>
              <div className="route-conditions-title panel-titlebar">
                <span>
                  Conditions along this route
                  {multiHour && routeSummary.hour !== null && routeSummary.hour !== undefined
                    ? ` · ${hourSpan(routeSummary.hour)}` : ''}
                </span>
                {foldToggle('route', 'route conditions')}
              </div>
              <div className="route-conditions-stats">
                <div><span>{(routeSummary.trip?.lengthKm || 0).toFixed(2)}</span> km</div>
                <div><span>{Math.round((routeSummary.trip?.timeS || 0) / 60)}</span> min</div>
                <div><span>{routeSummary.worstLos || '—'}</span> worst LOS</div>
                <div><span>{Math.round((routeSummary.coverage || 0) * 100)}%</span> modelled</div>
              </div>
              {(() => {
                const worst = (routeSummary.segments || [])
                  .filter((s) => s.modelled)
                  .sort((a, b) => b.vc - a.vc)
                  .slice(0, 3);
                if (worst.length === 0) {
                  return (
                    <div className="route-conditions-note">
                      No segment of this route is in the modelled network.
                    </div>
                  );
                }
                return (
                  <>
                    <div className="route-conditions-sub">Busiest segments</div>
                    {worst.map((s) => (
                      <div className="route-seg" key={s.wayId}>
                        <span
                          className="route-seg-swatch"
                          style={{ background: `rgb(${losColor(s.vc, 255).slice(0, 3).join(',')})` }}
                        />
                        <span className="route-seg-name">{s.name || `way ${s.wayId}`}</span>
                        <span className="route-seg-vc">{s.vc.toFixed(2)}</span>
                      </div>
                    ))}
                  </>
                );
              })()}
              {routeSummary.coverage < 1 && (
                <div className="route-conditions-note">
                  {(100 - (routeSummary.coverage || 0) * 100).toFixed(1)}% of the distance is on
                  roads the pipeline does not model (drawn grey).
                </div>
              )}
            </div>
          )}
        </div>

        {/* Confirmation popup for map click selection */}
        {pendingPoint && (() => {
          const pos = getPopupScreenPosition();
          if (!pos) return null;
          return (
            <div
              className="map-click-popup"
              style={{
                left: pos.x,
                top: pos.y,
                transform: 'translate(-50%, -120%)'
              }}
            >
              <div className="popup-content">
                <span>{pendingPoint.type === 'start' ? 'Set as Starting Point?' : 'Set as Destination?'}</span>
                <div className="popup-buttons">
                  <button className="popup-btn confirm" onClick={handleConfirmPoint} title="Confirm">
                    <FaCheck size={12} />
                  </button>
                  <button className="popup-btn cancel" onClick={handleCancelPoint} title="Cancel">
                    <FaTimes size={12} />
                  </button>
                </div>
              </div>
              <div className="popup-arrow"></div>
            </div>
          );
        })()}

        {showTransitInvestment && !inv && (
          <div className="inv-panel inv-loading" style={invPlace} aria-live="polite">
            {invError ? (
              <>
                <div className="overlay-legend-error">Transit investment data could not be loaded ({invError}).</div>
                <button type="button" className="inv-retry" onClick={() => setInvRetry((n) => n + 1)}>Try again</button>
              </>
            ) : 'Loading transit investment data…'}
          </div>
        )}
        {invShown && (
          <TransitInvestmentPanel
            lines={inv.lines} order={invOrder} metric={invMetric} onMetricChange={changeInvMetric} meta={inv.meta} sel={invPick.sel} cum={invPick.cum} stats={invStats}
            mode={invMode} onModeChange={setInvMode} target={invTarget} onTargetChange={changeInvTarget}
            boost={invBoost} onBoostChange={changeInvBoost} onClose={onCloseTransitInvestment}
            sameBudget={invEq} sameBudgetError={invEqError} onSameBudgetRetry={() => setInvEqRetry((n) => n + 1)}
            preset={invPreset} onPresetChange={applyInvPreset}
            view={invView} onViewChange={setInvView} upgrades={inv.upgrades} rank={inv.rank}
            panelRef={invPanelRef} style={invPlace} onMouseDown={handleInvDragStart}
            className={`${isInvDragging ? 'dragging' : ''} ${folded('inv')}`}
            collapseToggle={foldToggle('inv', 'Transit investment')}
          />
        )}

        <DeckGL
            initialViewState={viewState}
            onViewStateChange={({ viewState }) => {
              setViewState(viewState);
              if (showFacilities) {
                debouncedFacilityFetch(viewState);
              }
              if (showHexagons) {
                debouncedGridBbox(viewState);
              }
            }}
            controller={true}
            layers={drawnLayers}
            pickingRadius={8}
            onClick={handleMapClick}
            getCursor={({ isHovering }) => {
              if (showNetworkEditor) return isHovering ? 'pointer' : 'grab';
              return macroDrawingMode !== 'none' || (showShortestPath && getCurrentSelectionMode()) ? 'crosshair' : 'grab';
            }}
        >
          <Map
              initialViewState={viewState}
              mapStyle="mapbox://styles/mapbox/dark-v10"
              // Required by the 511 Data Agreement for the Muni schedules in Valhalla's tiles.
              customAttribution='<a href="https://511.org" target="_blank" rel="noopener noreferrer">Bay Area transit: data provided by 511.org</a>'
          />
        </DeckGL>
      </div>
  );
}

export default MapContainer; 